import React from 'react';
import {Map} from 'immutable';
import {first} from 'lodash';

import {ActionType} from '@packages/utilities/create-action';
import {
  handlePaste,
  type ContentEditorClipboardEvent,
} from './ContentEditorClipboardHelper';
// Local imports from ContentEditorUi
import {
  parseNativeSelection,
  applySelectionFromRecord,
  clearNativeSelection,
  getKeyboardShortcut,
  isPlainTextPasteShortcut,
  getNativeAnchorText,
  getTextShortcut,
  parseSelectionFromClickEvent,
  getImgNodeRects,
  getNodeIdFromNativeNode,
  getNativeIdFromNativeNode,
  preserveScrollAndFocus,
  safelyStopEditing,
  updateOverrideFieldValue,
  isEditingMultiLineNodeInInstance,
  hasMatchingMarkdownInCurrentNode,
  getMarkdownKey,
  getUpdatedItemFieldValue,
  getSelectionCorrectedForSkippedNode,
} from './ContentEditorHelper';
import {
  getTopFrameNativeNodeByNativeIdPath,
  getCanvasFrameNativeNodeByNativeIdPath,
} from './CanvasHelper';
import {registerContentEditorExitHandler} from './ContentEditorExitBridge';
import {
  selectionUpdated,
  charsInserted,
  mouseDown,
  mouseUp,
  paste,
  keyDown,
  openComponentSettings,
  keyboardShortcutPressed,
  textShortcutActivated,
  markdownFormattingActivated,
  imageSpinnerPositionsChanged,
  undo,
  redo,
  textValueReplaced,
  clientRectUpdated,
  imageImportFinished,
  imageUploadedWithError,
  imageImportFailed,
  imageImportStarted,
  cut,
  textOfNodeSelectedInInstanceEditingEnded,
  contentEditorValidationErrorShown,
  contentEditorModalClosed,
  contentEditorModalOpened,
  startOnCanvasEditSave,
  dynFieldSaveOnCanvasError,
  successOnCanvasEditSave,
  discardOnCanvasEditSave,
  type SelectionUpdatedActionCreator,
  type CharsInsertedActionCreator,
  type MouseDownActionCreator,
  type MouseUpActionCreator,
  type PasteActionCreator,
  type KeyDownActionCreator,
  type KeyboardShortcutPressedActionCreator,
  type TextShortcutActivatedActionCreator,
  type MarkdownFormattingActivatedActionCreator,
  type ImageSpinnerPositionsChangedActionCreator,
  type UndoActionCreator,
  type RedoActionCreator,
  type TextValueReplacedActionCreator,
  type ClientRectUpdatedActionCreator,
  type ImageUploadFinishedActionCreator,
  type ImageUploadedWithErrorActionCreator,
  type ImageImportStartedActionCreator,
  type ImageImportFinishedActionCreator,
  type ImageImportFailedActionCreator,
  type CutActionCreator,
  type TextOfNodeSelectedInInstanceEditingEndedActionCreator,
  type DynFieldModifiedOnCanvasActionCreator,
  type ContentEditorValidationErrorShownActionCreator,
  type ContentEditorModalOpenedActionCreator,
  type ContentEditorModalClosedActionCreator,
  type OnCanvasSaveStartedActionCreator,
  type DynFieldSaveOnCanvasErrorActionCreator,
  type OnCanvasSaveSuccessActionCreator,
  type OnCanvasSaveDiscardedActionCreator,
} from './ContentEditorActionCreators';
import {
  findHighestEmptyFormatAncestor,
  findNextTextOrLineBreak,
  findPreviousTextOrLineBreak,
  findRemoteImages,
  handleTypoSelectionBleed,
  isHiddenFromContentEditable,
  shouldKeepNativeRenderForTyping,
  shouldStripPastedComponents,
} from './ContentEditorStoreUtils';
import {VALID_NUMBER_CHARS, type ShortcutsType} from './constants';
import {type OnCanvasSaveErrorPayload} from './types';
import {
  slashCommandActivated,
  contentEditorAddMenuFocusSet,
  aiToolbarPositionSet,
  keyUp,
  contentEditorComponentValidationFailed,
  contentEditorMenuToggled,
} from './actions';
// Imports from ContentEditor
import {
  getSelectedSubtree,
  reifySelectionRecord,
  assertNodeSelectionRecordType,
  assertNodeSelectionType,
  assertRangeSelectionRecordType,
  getClientRect,
  CONTENT_EDITOR_MENUS,
  isCollapsed,
  isFormatTogglePending,
  caretIsAtStartOfTextNode,
  caretIsInStringElement,
  caretIsAtTheEndOfALink,
  clientRectChanged,
  handleFigcaptionSelection,
  handleFigureEndingSelection,
  handleRangeSelectionAtSubtreeRoot,
  hasCaretMoved,
  getCharBeforeCaret,
  isFormatElement,
  isRichTextNonFormatTypographic,
  isEditingCMSOnCanvas,
  getTextInputEditContext,
  shouldProfileTextInputLatency,
  isEditingRichTextComponentProp,
  isEditingRichTextComponentPropOverride,
  isNumberEditing,
  getTargetElement,
  moveCaretToDeepStartOfNode,
} from '@packages/systems/ContentEditor';
import {type ContentEditorViewportAdapter} from '@packages/systems/ContentEditorViewportAdapter';
import {
  TEXT_NODE,
  removeInstanceInFavorOfWIdAttr,
  getWindow,
} from '@packages/shared/utilities/DomUtils';
import {
  ENTER,
  SPACE,
  BACKSPACE,
  DELETE,
  TAB,
  ESC,
  RIGHT,
  DOWN,
  isArrow,
  isMetaKey,
} from '@packages/utilities/key-utils';
import {
  SHORTCUTS,
  EDIT_STYLES,
  TOOLBARS,
} from '@packages/shared/constants/ContentEditor';
import type {
  SaveCollectionItemFieldResolution,
  ParsedSelectionType,
} from '@packages/shared/types/ContentEditor';
import {pipe, constantTrue, constantFalse} from '@packages/utilities/fp/utils';
import {
  ContentEditorActive,
  getContentEditorActive,
} from '@packages/shared/states';
import type {ContentEditorLiveSnapshot} from '@packages/systems/designer/validation';
import {importFile} from '@packages/systems/assets-designer';
import {isNativeIdPath} from '@packages/shared/render/utils/ContentEditorUtils';
import {
  matchExpression,
  Component,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {
  AtomNormalizedRender,
  ExpansionProps,
  freshEnvForReifiedSubtree,
  useDispatch,
  useRuntimeEnvironment,
  useSite,
  useEditTargetFieldIdPath,
  useContentEditorViewportAdapter,
  useContentEditorFrameIdWhen,
  useContentEditorLiveSnapshot,
  type GetContentEditorLiveSnapshot,
} from '@packages/systems/render';
import {Tree} from '@packages/systems/tree';
import {
  getRelativePath,
  getInnermostInstanceRootElement,
  findElementDescendant,
  isPathContainingInstances,
  getChildren,
  getClosestListElement,
  isUserComponent,
  isCodeComponent,
} from '@packages/utilities/platform';
import {
  isRichText,
  isImageElement,
  isStringElement,
  isLinkElement,
  isTypographic,
  isLineBreakElement,
} from '@packages/utilities/elementTypeUtils';
import {
  getNativeNodeByNativeIdPath,
  type NativeId,
  getGuidNullable,
} from '@packages/systems/core/utils/NativeIdUtils';
import {isDynamoChildrenBinding} from '@packages/systems/dynamo/wfdl';
import {
  type Expression,
  type ExpressionElement,
  EElement,
} from '@packages/systems/wfdl-ast';
import {getDynBindingsFromElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {getElementBindingDataPaths} from '@packages/systems/dynamo/utils/BindingUtils';
import {confirm} from '@packages/systems/dialog';
import {
  TelemetryEventLabel,
  TextInputLatencyProfiler,
  telemetry,
} from '@packages/systems/designer-telemetry';
import {SiteType} from '@packages/systems/core/records/Site';
import {WEBFLOW_COMPONENT_ROOT_ATTR} from '@packages/systems/core/constants/SharedConfig';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {generateGuid} from '@packages/utilities/guid';
import {ClientLatencyTools} from '@packages/systems/designer/performance-tools';
import {
  isActiveLocalePrimary,
  undoIfErrorCaught,
  getInlineMarkdownTypeInText,
} from './ContentEditorUtils';
import {ContentEditorErrorBoundary} from './ContentEditorErrorBoundary';

const PAGE_HTML_WITHIN_CANVAS_SELECTOR = 'html[data-wf-site][data-wf-page]';

const createPlainTextClipboardEvent = (
  clipboardText: string
): ContentEditorClipboardEvent => ({
  clipboardData: {
    types: ['text/plain'],
    getData: (type: string) => (type === 'text/plain' ? clipboardText : ''),
  },
});

/** Clicks inside these nodes do not end Content Editor editing (see onDocMouseDown). */
const CONTENT_EDITOR_OUTSIDE_CLICK_EXEMPT_SELECTORS = [
  '.w-content-editor-toolbar-wrapper',
  '[data-wf-modal-id="discard-changes-modal"]',
  '[data-wf-modal-id="remote-save-dialog"]',
  '[data-wf-modal-id="item-action-dialog"]',
  '.add-content-editor-interactable',
  '[data-automation-id="left-sidebar-symbols-button"]',
  '[data-automation-id="components-list-wrapper"]',
  '[data-automation-id="component-search-input"]',
  // Rich Text property override modal. Rendered in a portal; without this,
  // clicks inside the modal look "outside" and tear down the parent Content
  // Editor before the override can be edited.
  '[data-automation-id="modal-richtext-editor-container"]',
  // Settings panel property menu (popover behind "Reset to default property
  // value"). Also portaled; without this, the click tears down the parent
  // Content Editor before the reset dispatch applies, leaving the override
  // in place.
  '[data-automation-id="settings-panel-property-menu"]',
] as const;

const CONTENT_EDITOR_OUTSIDE_CLICK_EXEMPT_IS = `:is(${CONTENT_EDITOR_OUTSIDE_CLICK_EXEMPT_SELECTORS.join(
  ','
)})`;

/** The CMS topbar button opens DM directly from canvas. */
const DATA_MODE_BUTTON_SELECTOR = '[data-automation-id="data-mode-button"]';

/** True if target is inside exempt UI (or a portaled control whose trigger is). */
const clickIsContentEditorInteractable = (target: HTMLElement): boolean => {
  const PORTAL_SELECTOR = '[data-floating-ui-focusable], [role="dialog"]';
  let portalAncestor: Element | null = null;

  for (let el: HTMLElement | null = target; el; el = el.parentElement) {
    if (el.matches(CONTENT_EDITOR_OUTSIDE_CLICK_EXEMPT_IS)) return true;
    if (el.matches(PORTAL_SELECTOR)) portalAncestor = el;
  }

  if (!portalAncestor) return false;

  const portalId = portalAncestor.getAttribute('id');
  if (!portalId) return false;

  const triggerViaAria = document.querySelector(
    `[aria-controls="${CSS.escape(portalId)}"]`
  );

  if (triggerViaAria) return true;
  const triggerViaLegacy = portalId.endsWith('-content')
    ? document.querySelector(
        `[data-focus-return="${CSS.escape(
          portalId.slice(0, -'-content'.length) + '-root'
        )}"]`
      )
    : null;

  return Boolean(triggerViaLegacy);
};

type ID = string;

export type PropsType = {
  render: AtomNormalizedRender;
  renderProps: ExpansionProps;
  isEditingThisInstance: boolean;
  isEditing: boolean;
  nativeId?: NativeId;
  isEditingSelectedNodeInInstance?: boolean;
  dispatch: (action: {type: string; payload: unknown}) => void;
  site?: SiteType;
  runtimeEnvironment?: Environment;

  // Optional prop to indicate whether we should report latency for this instance
  // of the ContentEditor. If not provided, no latency will be reported.
  latencyProfilerEventName?: string;
  editTargetFieldIdPath: string[] | null;
  contentEditorViewportAdapter: ContentEditorViewportAdapter;
  contentEditorFrameId?: string | null;
  getContentEditorLiveSnapshot?: GetContentEditorLiveSnapshot;
};

type Action =
  | ActionType<SelectionUpdatedActionCreator>
  | ActionType<CharsInsertedActionCreator>
  | ActionType<MouseDownActionCreator>
  | ActionType<MouseUpActionCreator>
  | ActionType<PasteActionCreator>
  | ActionType<KeyDownActionCreator>
  | ActionType<typeof keyUp>
  | ActionType<KeyboardShortcutPressedActionCreator>
  | ActionType<TextShortcutActivatedActionCreator>
  | ActionType<MarkdownFormattingActivatedActionCreator>
  | ActionType<ImageSpinnerPositionsChangedActionCreator>
  | ActionType<UndoActionCreator>
  | ActionType<RedoActionCreator>
  | ActionType<TextValueReplacedActionCreator>
  | ActionType<ClientRectUpdatedActionCreator>
  | ActionType<ImageUploadFinishedActionCreator>
  | ActionType<ImageUploadedWithErrorActionCreator>
  | ActionType<ImageImportStartedActionCreator>
  | ActionType<ImageImportFinishedActionCreator>
  | ActionType<ImageImportFailedActionCreator>
  | ActionType<CutActionCreator>
  | ActionType<TextOfNodeSelectedInInstanceEditingEndedActionCreator>
  | ActionType<DynFieldModifiedOnCanvasActionCreator>
  | ActionType<ContentEditorValidationErrorShownActionCreator>
  | ActionType<ContentEditorModalOpenedActionCreator>
  | ActionType<ContentEditorModalClosedActionCreator>
  | ActionType<DynFieldSaveOnCanvasErrorActionCreator>
  | ActionType<OnCanvasSaveStartedActionCreator>
  | ActionType<OnCanvasSaveSuccessActionCreator>
  | ActionType<OnCanvasSaveDiscardedActionCreator>
  | ActionType<typeof slashCommandActivated>
  | ActionType<typeof contentEditorAddMenuFocusSet>
  | ActionType<typeof aiToolbarPositionSet>
  | ActionType<typeof contentEditorComponentValidationFailed>
  | ActionType<typeof contentEditorMenuToggled>
  | ActionType<typeof openComponentSettings>;

let adjacentTextNodesLogged = false;

const canAllowNativeRender = (state: ContentEditorActive) => {
  const {selection} = state;

  const rangeSelection = assertRangeSelectionRecordType(selection);

  // If we have a collapsed range selection, and *none* of the following edge
  // cases that would require a ReactRender are true, then we can allow a
  // nativeRender.
  return (
    rangeSelection &&
    isCollapsed(rangeSelection) &&
    // If there's a formatToggle pending then the next character will be
    // wrapped or unwrapped so requires a ReactRender
    !isFormatTogglePending(state) &&
    // If the caret is not a textNode eg: the caret is between two <br>s
    // we can't allow nativeRender.
    caretIsInStringElement(state) &&
    // When the caret is at the end of a link node, the  browser will do
    // horrible things to us. Eg: pressing space at the end of a link will add
    // an invisible textNode with the space just outside the link that's next to
    // whatever textNode comes after the link but separate and distinct but not
    // in the store and not rendered in devTools elements tab.
    !caretIsAtTheEndOfALink(state) &&
    // It's also possible to get the caret into the first position in a textNode
    // that immediately follows a link. To avoid testing for all the nested
    // cases where this can happen we just block native render if it's at the
    // start of any textNode. This shouldn't affect typing since it'll only ever
    // be at the start of a textNode for a single character.
    !caretIsAtStartOfTextNode(rangeSelection)(state)
  );
};

const canAllowNativeDeletion = (
  state: ContentEditorActive,
  key: typeof BACKSPACE | typeof DELETE,
  nativeSelection: Selection | null | undefined,
  isInDataManagerContext: boolean
) => {
  const {subtree, selection} = state;

  // Cheap structural disqualifier first: plain text / non-RT fields are
  // never candidates, so skip the rest of the policy and selection checks.
  if (!isRichText(subtree)) return false;

  const rangeSelection = assertRangeSelectionRecordType(selection);
  const nativeAnchorNode = nativeSelection?.anchorNode;

  // Deletion has a stricter exclusion policy than typing: in addition to the
  // typing exclusions (CMS-bound, number editing) we also force React on
  // component-prop edits because instance overrides are more delicate to
  // update through the native delete pipeline than through additive typing,
  // and on Data Manager edits where the item-edit-pane and modal flows
  // depend on the React reconcile to keep the DOM in sync with the store
  // after structural delete (paragraph merge, empty paragraph cleanup).
  if (
    isEditingCMSOnCanvas(state) ||
    isNumberEditing(state) ||
    isEditingRichTextComponentProp(state) ||
    isInDataManagerContext
  ) {
    return false;
  }

  if (
    !rangeSelection ||
    !isCollapsed(rangeSelection) ||
    !nativeSelection?.isCollapsed ||
    nativeAnchorNode !== nativeSelection.focusNode ||
    nativeAnchorNode?.nodeType !== TEXT_NODE ||
    isFormatTogglePending(state)
  ) {
    return false;
  }

  if (
    'wholeText' in nativeAnchorNode &&
    'nodeValue' in nativeAnchorNode &&
    nativeAnchorNode.nodeValue !== nativeAnchorNode.wholeText
  ) {
    return false;
  }

  const textNodeId = getNodeIdFromNativeNode({
    subtree,
    nativeNode: nativeAnchorNode,
  });
  const textElement = textNodeId
    ? Expressions.getElementById(EElement(subtree), textNodeId)
    : null;

  if (!textNodeId || !textElement || !isStringElement(textElement)) {
    return false;
  }

  if (
    Expressions.closestElement(EElement(subtree), textNodeId, isLinkElement)
  ) {
    return false;
  }

  const nativeValue = nativeAnchorNode.nodeValue;
  if (nativeValue === null) {
    return false;
  }

  // Forward deleting at the start of a text node, when there is a preceding
  // text/line-break in the same block and either that node or the preceding
  // one sits inside a format element, moves the native caret to resolve on
  // the wrong side of the format-element boundary after the mutation:
  //   e.g. <p>12<em>34</em></p> starting between "2" and "3" (deletes "3")
  //   e.g. <p><em>34</em>56</p> starting between "4" and "5" (deletes "5")
  // onInput's reconciliation then reads the unchanged content on that other
  // side instead of the node that actually changed, so the Flux subtree
  // falls one character behind the DOM. We need to early return to force
  // the Flux-dispatched path.
  if (key === DELETE && nativeSelection.anchorOffset === 0) {
    const enclosingBlock =
      Expressions.closestElement(
        EElement(subtree),
        textNodeId,
        isRichTextNonFormatTypographic
      ) ?? subtree;

    const precedingNode = findPreviousTextOrLineBreak(
      enclosingBlock,
      textNodeId
    );

    if (precedingNode) {
      const closestFormatAncestor = Expressions.closestElement(
        EElement(subtree),
        textNodeId,
        isFormatElement
      );
      const precedingFormatAncestor = Expressions.closestElement(
        EElement(subtree),
        precedingNode.id,
        isFormatElement
      );

      if (closestFormatAncestor || precedingFormatAncestor) {
        return false;
      }
    }
  }

  // Mirror of the above: backward deleting a sibling's own first character
  // via BACKSPACE, when that sibling directly follows a format element,
  // moves the native caret to resolve inside the format element's own
  // content instead of the sibling that actually changed. onInput's
  // reconciliation then reads that unchanged format-element content and
  // never dispatches, so the Flux subtree falls one character behind. We
  // need to early return to force the Flux-dispatched path.
  if (key === BACKSPACE && nativeSelection.anchorOffset === 1) {
    const enclosingBlock =
      Expressions.closestElement(
        EElement(subtree),
        textNodeId,
        isRichTextNonFormatTypographic
      ) ?? subtree;

    const precedingNode = findPreviousTextOrLineBreak(
      enclosingBlock,
      textNodeId
    );

    if (precedingNode) {
      // Any preceding text/line-break inside a format element means this
      // backspace can land the native caret on content that didn't change
      // (either the preceding format element's own last character, or earlier
      // content in the same format run), so skip native backspace to force the
      // Flux-dispatched path.
      //   e.g. <p><em>12</em>34</p> starting between "3" and "4"
      //   e.g. <p><strong>12<em>34</em></strong></p> starting between "3" and "4"
      const precedingFormatAncestor = Expressions.closestElement(
        EElement(subtree),
        precedingNode.id,
        isFormatElement
      );

      if (precedingFormatAncestor) {
        return false;
      }
    }
  }

  // Deleting a sibling's sole remaining character (via DELETE from its start
  // or BACKSPACE from its end) when that sibling directly precedes a format
  // element causes the caret to land on the sibling whose content didn't change,
  // so the browser prunes the now-empty node without Flux/React finding out.
  if (
    nativeValue.length === 1 &&
    ((key === DELETE && nativeSelection.anchorOffset === 0) ||
      (key === BACKSPACE && nativeSelection.anchorOffset === 1))
  ) {
    const enclosingBlock =
      Expressions.closestElement(
        EElement(subtree),
        textNodeId,
        isRichTextNonFormatTypographic
      ) ?? subtree;

    const followingNode = findNextTextOrLineBreak(enclosingBlock, textNodeId);

    if (followingNode) {
      const followingFormatAncestor = Expressions.closestElement(
        EElement(subtree),
        followingNode.id,
        isFormatElement
      );

      if (followingFormatAncestor) {
        return false;
      }
    }

    // Deleting the last character of the only text/line-break node in a block
    // empties the paragraph. The native path skips the keydown dispatch in the
    // keydown handler, so the empty-paragraph cleanup in
    // handleEmptyTextNodeAfterContentRemoval never runs and a stray empty <p>
    // is left in the DOM. Force the Flux-dispatched path.
    if (
      followingNode === null &&
      findPreviousTextOrLineBreak(enclosingBlock, textNodeId) === null
    ) {
      return false;
    }
  }

  // If this keystroke would remove the last remaining character in the
  // text node, and that text node is the sole child of a format element
  // (sup/sub/strong/em/a/code/span) ancestor chain, force the
  // Flux-dispatched path so the empty-format-ancestor cleanup in
  // handleEmptyTextNodeAfterContentRemoval runs.
  if (
    nativeValue.length === 1 &&
    findHighestEmptyFormatAncestor(subtree, textElement)
  ) {
    return false;
  }

  return key === BACKSPACE
    ? nativeSelection.anchorOffset > 0
    : nativeSelection.anchorOffset < nativeValue.length;
};

const confirmDiscard = async ({
  onSubmit,
  onCancel,
}: {
  onSubmit: () => void;
  onCancel: () => void;
}) => {
  let didSubmit = false;

  await confirm({
    title: 'Discard changes?',
    content:
      "Unable to save with errors in the item you're editing. Do you want to discard the changes you have made to this CMS item?",
    onSubmit: () => {
      didSubmit = true;
      onSubmit();
    },
    onCancel,
    submit: {
      label: 'Discard',
      intent: 'danger',
    },
    'data-automation-id': 'discard-changes-modal-wrapper',
    'data-wf-modal-id': 'discard-changes-modal',
  });

  return didSubmit;
};

const setClipboardJsonData = (
  editState: ContentEditorActive,
  event: React.ClipboardEvent,
  components: Component.ComponentMap
) => {
  const element = getSelectedSubtree(editState);
  const jsonContentNodes = element
    ? new Tree(components).getElementAsJSONNodes(element)
    : [];

  event.clipboardData.setData(
    'application/json',
    JSON.stringify(jsonContentNodes)
  );
};

const setClipboardPlainAndHtmlData = (event: React.ClipboardEvent) => {
  const eventTarget = event.target as HTMLElement;
  const selection = eventTarget.ownerDocument.getSelection();

  if (!selection) {
    return;
  }

  const docFragmentOfSelectedContent = selection.getRangeAt(0).cloneContents();
  const plaintextContent = docFragmentOfSelectedContent.textContent;
  const selectedNodes = docFragmentOfSelectedContent.childNodes;
  // We keep selected plain text(e.g. inside Paragraph) only in text/plain format, which is different from
  // native behavior, where plain text is wrapped and stored in text/html too
  const htmlContent =
    selectedNodes.length === 1 && selectedNodes[0]?.nodeType === TEXT_NODE
      ? undefined
      : [].slice
          .call(selectedNodes)
          .map((n: HTMLElement) => n.outerHTML || n.nodeValue)
          .join('');

  if (plaintextContent) {
    event.clipboardData.setData('text/plain', plaintextContent);
  }
  if (htmlContent) {
    event.clipboardData.setData('text/html', htmlContent);
  }
};

class ContentEditor extends React.Component<PropsType> {
  latencyProfiler: TextInputLatencyProfiler | null = null;
  inFlightCmsSave: Promise<boolean> | null = null;
  unregisterContentEditorExitHandler: (() => void) | null = null;

  // Note: Ideally this would be handled in a selection engine, but there's no
  // acceptable way to pass the nativeSubtree reference to the engine after react renders it.
  // And we need the nativeSubtree to apply the focus and find the correct window / iframe. ie: canvas. vs. dm
  // ie: Due to side-effects, the native DOM/window reference can't be sent in an action's payload
  nativeSubtree: HTMLElement | null | undefined = null;

  // We want to track composition events if the user is typing a character using
  // IME input (used in Korean, Chinese and Japanese keyboards) or an accent
  // followed by a vowel. If a composition event is taking place, the component
  // should update until the user has finished the character composition. The
  // resolution of this composition might take an extra step depending on which
  // the next key is, so we need to check first if it has been resolved in order
  // to mark it as finished.
  isComposing = false;
  compositionJustResolved = false;
  compositionFinished = false;
  isSynchronizingNativeRenderBaseline = false;

  constructor(props: PropsType) {
    super(props);

    this.latencyProfiler = props.latencyProfilerEventName
      ? new TextInputLatencyProfiler(props.latencyProfilerEventName)
      : null;
  }

  getEditState(props?: PropsType): ContentEditorActive | undefined {
    const contentEditorState = (
      props || this.props
    ).renderProps.pluginsContext.get('contentEditor') as unknown as
      | undefined
      | ContentEditorActive;
    // We check for subtree here so that we only return a state, if we're editing ie:
    // if the store's state is populated.
    if (contentEditorState && contentEditorState.subtree) {
      return contentEditorState;
    } else {
      return undefined;
    }
  }

  getLiveEditState():
    | (ContentEditorLiveSnapshot & {contentEditorState: ContentEditorActive})
    | undefined {
    const liveSnapshot = this.props.getContentEditorLiveSnapshot?.();
    // Mirror getEditState: only treat the store snapshot as active when it
    // carries a subtree, so stale/inactive snapshots fall back to props.
    if (liveSnapshot?.contentEditorState.subtree) {
      return liveSnapshot as ContentEditorLiveSnapshot & {
        contentEditorState: ContentEditorActive;
      };
    } else {
      return undefined;
    }
  }

  dispatch = (action: Action) => {
    return this.props.dispatch(action);
  };

  dispatchAfterNativeRenderSync = (action: Action) => {
    const editState = this.getEditState();
    const shouldSyncCmsBoundNativeRender =
      editState?.allowNativeRender &&
      isEditingCMSOnCanvas(editState) &&
      shouldKeepNativeRenderForTyping(editState, {
        allowCmsBoundRichTextNativeRender: true,
      });

    if (shouldSyncCmsBoundNativeRender) {
      this.isSynchronizingNativeRenderBaseline = true;
      this.forceUpdate(() => {
        this.isSynchronizingNativeRenderBaseline = false;
        this.dispatch(action);
      });
    } else {
      this.dispatch(action);
    }
  };

  shouldComponentUpdate(nextProps: PropsType) {
    const editState = this.getEditState(nextProps);

    if (this.isComposing || this.compositionJustResolved) {
      // If the user is in the middle of typing a composite character, we don't
      // want to update the component, just wait until his IME input edition
      // ends to fire anything. We also want to wait a render to avoid the
      // keyboard from adding extra characters after the composition delay when
      // finishing a Korean composition event.
      return false;
    } else if (nextProps.isEditingThisInstance && editState) {
      const currentEditState = this.getEditState();
      const currentValidationError =
        this.props.renderProps.environmentContext.get(
          'contentEditorValidationError'
        );
      const nextValidationError = nextProps.renderProps.environmentContext.get(
        'contentEditorValidationError'
      );
      const currentHasVisibleError = Boolean(
        currentEditState?.hasServerError ||
        (currentEditState?.validationErrorShown && currentValidationError)
      );
      const nextHasVisibleError = Boolean(
        editState.hasServerError ||
        (editState.validationErrorShown && nextValidationError)
      );

      if (currentHasVisibleError !== nextHasVisibleError) {
        return true;
      }

      const {allowNativeRender} = editState;
      const useReactRender = !allowNativeRender;
      return useReactRender;
    } else {
      return true;
    }
  }

  componentDidMount() {
    if (this.props.isEditingThisInstance) {
      this.updateNativeSubtree();
      this.registerExitHandler();
    }
  }

  componentDidUpdate = undoIfErrorCaught((prevProps: PropsType) => {
    const justSwitchedToThisInstance =
      this.props.isEditingThisInstance && !prevProps.isEditingThisInstance;
    const justStartedEditingWhileOnThisInstance =
      this.props.isEditingThisInstance &&
      !prevProps.isEditing &&
      this.props.isEditing;
    const shouldRegisterExitHandler =
      justSwitchedToThisInstance || justStartedEditingWhileOnThisInstance;
    if (shouldRegisterExitHandler) {
      this.registerExitHandler();
    } else if (
      prevProps.isEditingThisInstance &&
      !this.props.isEditingThisInstance
    ) {
      this.unregisterExitHandler();
    }

    if (this.props.isEditingThisInstance) {
      const startingToEdit = !prevProps.isEditing && this.props.isEditing;
      if (startingToEdit) {
        this.updateNativeSubtree();
        this.addDocMouseEvents();
      }

      if (this.latencyProfiler && !this.isSynchronizingNativeRenderBaseline) {
        this.latencyProfiler.finish();
      }

      const {
        subtree: prevSubtree,
        selection: prevSelection,
        activeToolbar: prevActiveToolbar,
        shouldCloseAfterComponentAdd: prevShouldCloseAfterComponentAdd,
      } = this.getEditState(prevProps) || {};

      const {
        subtree,
        selection,
        activeUploads,
        breakOutElement,
        activeToolbar,
        linkOnlyMode,
        slashCommand,
        editingContentEditorEmbed,
        editingContentEditorCodeBlock,
        shouldCloseAfterComponentAdd,
      } = this.getEditState() || {};

      // Catch-all for mouse clicks and other non-keyboard selection changes:
      // hide the native caret when a node-selectable element is selected,
      // restore it otherwise. onKeyDown/onKeyUp handle the keyboard path
      // separately to avoid a visible flash between frames.
      if (this.nativeSubtree) {
        this.nativeSubtree.style.caretColor =
          selection?.type === 'Node' ? 'transparent' : '';
      }

      // If the Add Menu was closed but the focus wasn't removed, remove it
      if (
        prevActiveToolbar === CONTENT_EDITOR_MENUS.ADD_MENU &&
        !activeToolbar &&
        slashCommand?.isMenuFocused
      ) {
        this.dispatch(contentEditorAddMenuFocusSet({setFocus: false}));
      }

      if (linkOnlyMode) {
        // In linkonly mode we don't need to sync with the native caret, because
        // there isn't one.
        return;
      }

      if (prevActiveToolbar === 'link' && activeToolbar === 'format') {
        this.applySelection();
      }

      if (breakOutElement) {
        // Trigger a stop editing, the designer can then look at ContentEditorState

        // for state.breakOutElement and decide how to change the designer state (by selecting that node)
        // after unbinding.
        // BreakOut is only possible in simple text and there is no need to check if active uploads are present
        this.stopEditingText();
        return;
      }

      if (!prevProps.isEditing && this.nativeSubtree) {
        const focusedElement = this.getWindow()?.document.activeElement;
        const focusedComponentRoot =
          focusedElement &&
          this.nativeSubtree.contains(focusedElement) &&
          focusedElement.closest(`[${WEBFLOW_COMPONENT_ROOT_ATTR}]`);

        if (focusedComponentRoot && subtree) {
          // Editing just (re)started because focus moved to a component root
          // (eg. tabbing out of text, or between sibling roots) — that focus
          // is already exactly where it should be, so don't steal it back to
          // the field container. Select the root directly instead: the focus
          // event that put focus here fired before onFocus below could see
          // it, so that handler won't fire retroactively for it.
          const parsedSelectionFromEvent = parseSelectionFromClickEvent(
            subtree,
            {target: focusedComponentRoot, clientX: 0, clientY: 0},
            this.nativeSubtree,
            this.props.contentEditorViewportAdapter
          );
          if (parsedSelectionFromEvent) {
            this.dispatch(
              mouseDown({
                seedId: generateGuid(),
                parsedSelection: parsedSelectionFromEvent,
              })
            );
          }
        } else {
          // Note: if we don't do this when starting to edit, the native
          // window.selection() could point to any element on the canvas
          // which will cause errors
          preserveScrollAndFocus(this.nativeSubtree);
        }
      }

      // While there are active uploads re-measure uploading images whenever
      // there's a subtree change. This includes:
      // - editing content above the in-progress image
      // - changing the alignment of any figure that can affect the size/position
      //   of an in-progress one
      // - the preview blobUrl being set as part of the upload progress
      //   (Note: make sure to preload preview blobUrls before being setting them
      //   on the image)
      const uploadsNeedMeasuring = activeUploads?.find(
        (activeUpload) => !activeUpload.nodeClientRect
      );
      if (
        activeUploads &&
        activeUploads.size &&
        this.nativeSubtree &&
        (prevSubtree !== subtree || uploadsNeedMeasuring)
      ) {
        const imgNodeRects = getImgNodeRects(
          this.nativeSubtree,
          activeUploads,
          this.props.contentEditorViewportAdapter
        );
        this.dispatch(imageSpinnerPositionsChanged({imgNodeRects}));
      }

      if (this.isSynchronizingNativeRenderBaseline) {
        // The baseline render replaces the text node, so preserve the store selection instead of reading stale native state.
        this.applySelection();
        return;
      }

      const parsedSelection = this.getParsedNativeSelection();
      const selectionFromNative =
        parsedSelection && reifySelectionRecord(parsedSelection);

      const win = this.getWindow();

      if (!win) return;

      const contentDocument = win.document;

      const contentAndToolbarsInSameDocument =
        contentDocument === window.document;
      const focusedElement = contentDocument.activeElement;

      const isToolbarFocused = Boolean(
        focusedElement &&
        focusedElement.closest('.w-content-editor-toolbar-wrapper')
      );

      // In the designer-canvas the content is inside the canvas iframe, and the
      // toolbars are on the top-level document. In the data-managers both are
      // in the same document so focus() behaves differently. ie: each document
      // has a separate activeElement that is focused.
      if (
        contentAndToolbarsInSameDocument &&
        focusedElement !== this.nativeSubtree
      ) {
        if (isToolbarFocused) {
          // If the content and toolbars are on the same document, and one of the
          // ContentEditor toolbar inputs has focus, don't try to steal focus and
          // apply the content selection.
          return;
        } else if (this.nativeSubtree) {
          // We're about to apply the content selection, and something else is
          // trying to steal focus.

          // If we're adding a new element, we can safely lose focus, because the
          // editor will regain focus once the new element has been added.
          // This also allows us pass focus to the EmbedEditor modal.

          // Also, we want to pass focus if the HTML Embed or Code Block editor is open.
          if (
            activeToolbar === 'add' ||
            editingContentEditorEmbed ||
            editingContentEditorCodeBlock
          ) {
            return;
          }

          // Otherwise, we need to make sure the editor is focused before
          // applying the selection.
          preserveScrollAndFocus(this.nativeSubtree);
          this.applySelection();
          return;
        }
      }

      // In the data-manager we can tab between fields. When that happens
      // We set the selection at the beginning or end of the node and we just want
      // to apply the selection to the dom
      if (
        (!prevSubtree || prevSubtree.id !== subtree?.id) &&
        subtree &&
        !selectionFromNative &&
        selection
      ) {
        this.applySelection();
        return;
      }

      // Used to determine the direction of selection changes. ie: are we
      // updating the store from the native selection (eg: double/triple click)
      // or are we updating the nativeSelection from the store (eg: pressing
      // space key just before a space char to make the caret skip)
      const prevSelectionWasInSync =
        selectionFromNative &&
        !hasCaretMoved(selectionFromNative, prevSelection);
      const selectionChangedInStore =
        prevSelection && hasCaretMoved(prevSelection, selection);
      const nativeSelectionOutOfSync =
        selectionFromNative && hasCaretMoved(selectionFromNative, selection);

      if (
        // If the subtree changed we likely also had to move the caret/selection
        // so let's apply those changes to the native selection to keep them in
        // sync.
        (this.nativeSubtree && subtree !== prevSubtree) ||
        // If the native selection is in sync with the previous store selection,
        // and we made a change in the store and it's now out of sync, then
        // apply the store's selection deets to the native selection.
        (prevSelectionWasInSync &&
          selectionChangedInStore &&
          nativeSelectionOutOfSync)
      ) {
        this.applySelection();
      } else if (this.compositionFinished && nativeSelectionOutOfSync) {
        // If the user has finished the IME input composition event, the browser
        // will re-render the caret at the wrong anchor position, if this is the
        // case, we should apply the nativeSelection from the store.
        // Once the composition event is over and this.compositionFinished we
        // also want to stay at the right position for the caret
        this.compositionFinished = false;
        this.applySelection();
      } else if (
        !selectionChangedInStore &&
        nativeSelectionOutOfSync &&
        parsedSelection &&
        !linkOnlyMode
      ) {
        // If the native selection is now out of sync, and there was no change
        // in the store, then we want to grab the native selection and update
        // the store. (eg: with double or triple click)
        this.dispatch(selectionUpdated({parsedSelection}));
      } else if (
        (!selection ||
          (!assertRangeSelectionRecordType(selection)?.rangeClientRect &&
            !assertNodeSelectionRecordType(selection)?.nodeClientRect)) &&
        parsedSelection
      ) {
        // If the client rect was null, we wanted to wait for render and can now
        // update it from the parsedSelection
        const payload =
          parsedSelection.type === 'Range'
            ? {rangeClientRect: parsedSelection.rangeClientRect}
            : {nodeClientRect: parsedSelection.nodeClientRect};
        this.dispatch(clientRectUpdated(payload));
      }

      if (
        prevActiveToolbar === CONTENT_EDITOR_MENUS.AI_LOADING_BAR &&
        activeToolbar === CONTENT_EDITOR_MENUS.AI_EDIT_MENU
      ) {
        this.applySelection(this.props, true);
      }

      // If shouldCloseAfterComponentAdd was changed to true, close the content editor
      // and select the component. This lives in the component so we can ensure it doesn't
      // close until the component is added and the selection is set. This ensures the correct
      // subtree is saved and the component is selected after closing.
      if (
        shouldCloseAfterComponentAdd &&
        !prevShouldCloseAfterComponentAdd &&
        subtree
      ) {
        const isEditingRichText = isRichText(subtree);

        const editState = this.getEditState();

        if (
          editState &&
          isEditingRichText &&
          !isEditingCMSOnCanvas(editState) &&
          !isEditingRichTextComponentProp(editState)
        ) {
          this.stopEditingText();
        }
      }
    } else if (prevProps.isEditing && !this.props.isEditing) {
      // If we've stopped editing make sure to clear the caret selection from
      // the native dom Otherwise it'll just persist until the next node gets
      // bound and cause issues.
      const win = this.getWindow();
      if (!win) return;
      clearNativeSelection(win);

      // We're about to lose reference to the old subtree, make sure to cleanup
      // any events
      this.removeDocMouseEvents();
      this.unregisterExitHandler();
      this.nativeSubtree = null;
      this.shortcutBlurHack();
    }
  }, this.dispatch);

  componentWillUnmount() {
    this.unregisterExitHandler();

    if (this.props.isEditingThisInstance) {
      // Normally isEditing is just toggled, but in some cases like removing
      // all content in a TextBlock on the designer-canvas and clicking  out
      // will convert it to a regular div and unmount it instead of toggling
      // so just make sure in all those cases we cleanup those native event
      // listeners
      this.removeDocMouseEvents();
      // In the typical edit lifecyle we just toggle isEditing. ComponentWillUnmount only happens while isEditing is true
      // when editing simple-text TextBlocks and converting them between regular divs and textBlocks
      // or other operations that modify the root node when saving changes.
      this.shortcutBlurHack();

      // We cancel any ongoing latency reporting
      if (this.latencyProfiler) {
        this.latencyProfiler.cancel();
      }
    }
  }

  applySelection = undoIfErrorCaught(
    (props: PropsType, shouldUpdateAiToolbarPosition?: boolean) => {
      const editState = this.getEditState(props);
      const {nativeSubtree} = this;
      if (editState && nativeSubtree) {
        const {subtree, selection, editingNodeSettings} = editState;
        const rectFromState = getClientRect(selection);

        applySelectionFromRecord(
          selection,
          subtree,
          nativeSubtree,
          editingNodeSettings
        );

        // Now that the store selection has been applied to the native selection
        // we need to update the clientRect.
        // Note: The rect is used for toolbar and UI layer placement
        const parsedSelection = this.getParsedNativeSelection(props);
        if (parsedSelection) {
          const parsedClientRect = getClientRect(parsedSelection);

          if (
            !rectFromState ||
            (rectFromState &&
              clientRectChanged(rectFromState, parsedClientRect))
          ) {
            const payload =
              parsedSelection.type === 'Range'
                ? {rangeClientRect: parsedSelection.rangeClientRect}
                : {nodeClientRect: parsedSelection.nodeClientRect};
            this.dispatch(clientRectUpdated(payload));

            if (shouldUpdateAiToolbarPosition) {
              if (!payload?.rangeClientRect) return;
              this.dispatch(aiToolbarPositionSet(payload));
            }
          }
        }
      }
    },
    this.dispatch
  );

  getParsedNativeSelection(props?: PropsType) {
    const editState = this.getEditState(props);
    // If we're in the Editor, this.getWindow() won't work
    const win = this.getWindow() || window;
    const nativeSelection = win.getSelection();

    if (editState && this.nativeSubtree) {
      const {subtree} = editState;
      if (
        nativeSelection &&
        nativeSelection.anchorNode !== this.nativeSubtree &&
        !this.nativeSubtree.contains(nativeSelection.anchorNode)
      ) {
        // If the caret is not inside the nativeSubtree, it means the native caret has
        // run off somewhere. This can happen eg: when adding a page link where the link
        // toolbar sort of ends up focused, in the Designer dm at the time of writing
        // There's a focusable element way up in the tools layer that ends up actually
        // being focused. Since that's not a valid selection (and other things will
        // set/restore the correct selection later) we can just return null here
        // and let the call site decide what to do about it.
        return null;
      } else {
        return parseNativeSelection(
          subtree,
          this.nativeSubtree,
          {
            nativeSelection,
          },
          this.props.contentEditorViewportAdapter
        );
      }
    }
  }

  // Use this before getParsedNativeSelection in cases where you want to get a
  // refined native selection
  refineNativeSelection(props?: PropsType) {
    const editState = this.getEditState(props);
    if (editState) {
      const {subtree} = editState;
      const parsedSelection = this.getParsedNativeSelection();

      if (!parsedSelection) {
        // If there is no parsed selection, we're returning to the ContentEditor after
        // focusing elsewhere, so we want to deselect everything.

        // Example: add an embed to a CMS item rich text field, and close the modal before adding content.
        // If this early return is removed, the selection refinement will fail, which will trigger
        // an `undo` action, removing the newly added (empty) embed. Instead, we just want
        // to have no selection at all, so that the embed remains.
        return;
      }

      const selectionFromNative = reifySelectionRecord(parsedSelection);
      const refinedSelectionFromNative = pipe([
        // Note: handleTypoSelectionBleed must come before
        // handleFigureEndingSelection or triple clicking a heading that's just
        // before a figure will select the figure.
        handleTypoSelectionBleed(subtree),
        handleFigcaptionSelection(subtree),
        handleFigureEndingSelection(subtree),
        handleRangeSelectionAtSubtreeRoot(subtree),
      ])(selectionFromNative);

      if (
        refinedSelectionFromNative !== selectionFromNative &&
        this.nativeSubtree
      ) {
        applySelectionFromRecord(
          refinedSelectionFromNative,
          subtree,
          this.nativeSubtree
        );
      }
    }
  }

  updateNativeSubtree = undoIfErrorCaught(() => {
    // We don't expect react to generate a new DOMNode reference while we're
    // editing, so this is effectively the first render right after editing
    // starts

    // Note: When you click out of a TextBlock removing all the text and
    // clicking out will convert it to a regular div, and visa-versa. In
    // either case it will reach here, even though we're unbinding.

    // Note: When editing text in a symbol or dynlist, we want to make sure
    // we're editing the correct native element, so we use the nativeId
    const editTargetNativeIdPath = this.getEditTargetNativeIdPath();
    this.nativeSubtree =
      getTopFrameNativeNodeByNativeIdPath(editTargetNativeIdPath) ||
      getCanvasFrameNativeNodeByNativeIdPath(
        editTargetNativeIdPath,
        this.props.contentEditorFrameId
      );
  }, this.dispatch);

  getWindow() {
    return getWindow(this.nativeSubtree);
  }

  getClipboard() {
    return this.getWindow()?.navigator.clipboard ?? window.navigator.clipboard;
  }

  onDocMouseUp = (event: MouseEvent) => {
    const eventTarget = event.target as HTMLElement;

    // If you start the mouse down inside and end up outside we want to still
    // run the mouseup to complete the action. But if you just click outside
    // then you want to unmount the ContentEditor so we don't need to call
    // mouseUp
    if (
      this.nativeSubtree &&
      !this.nativeSubtree.contains(eventTarget) &&
      !eventTarget.closest('.w-content-editor-toolbar-wrapper') &&
      this.lastMouseDownInside === true
    ) {
      this.onMouseUp();
    }
  };

  setClipboardData = (event: React.ClipboardEvent) => {
    const editState = this.getEditState();
    if (!editState) {
      return;
    }
    event.preventDefault();

    setClipboardJsonData(editState, event, this.props.renderProps.components);
    setClipboardPlainAndHtmlData(event);
  };

  onCopy = (event: React.ClipboardEvent) => {
    this.setClipboardData(event);
  };

  onCut = (event: React.ClipboardEvent) => {
    this.setClipboardData(event);
    this.dispatchAfterNativeRenderSync(cut(generateGuid()));
  };

  lastMouseDownInside = false;

  onDocMouseDown = (event: MouseEvent) => {
    const eventTarget = event.target as HTMLElement;
    const editState = this.getEditState();
    const editingContentEditorEmbed = Boolean(
      editState && editState.editingContentEditorEmbed
    );
    const editingContentEditorCodeBlock = Boolean(
      editState?.editingContentEditorCodeBlock
    );
    const isAIFeedbackModalOpen = Boolean(
      editState?.aiData.isFeedbackModalOpen
    );
    const editingNodeSettings = Boolean(editState?.editingNodeSettings);
    const isDataModeButtonClick = Boolean(
      eventTarget.closest(DATA_MODE_BUTTON_SELECTOR)
    );
    if (
      !editingContentEditorEmbed &&
      !editingContentEditorCodeBlock &&
      !isAIFeedbackModalOpen &&
      // Topbar mode switches should still flush/save canvas edits even when a
      // sidebar settings transaction is active.
      (!editingNodeSettings || isDataModeButtonClick) &&
      this.nativeSubtree &&
      !this.nativeSubtree.contains(eventTarget) &&
      !clickIsContentEditorInteractable(eventTarget)
    ) {
      event.preventDefault();
      this.safelyStopEditing({
        eventIsOnCanvas: Boolean(
          eventTarget.closest(PAGE_HTML_WITHIN_CANVAS_SELECTOR)
        ),
      });
      this.lastMouseDownInside = false;
    } else {
      this.lastMouseDownInside = true;
    }
  };

  onBeforeUnload = (event: Event) => {
    const editState = this.getEditState();
    if (editState) {
      const {activeUploads, undoStack} = editState;
      let message;

      if (activeUploads.size) {
        message =
          '▶▶▶ YOU HAVE UNSAVED CHANGES! ◀◀◀ \n \nThere are uploads still in progress.';
      } else if (undoStack.size) {
        message =
          '▶▶▶ YOU HAVE UNSAVED CHANGES! ◀◀◀ \n \nSaving has not completed for your most recent changes.';
      }

      // The user might not realize they need to 'click out' of the text element
      // if they do want to save their changes, so we just always stop editing /
      // save here unless they have uploads in progress, if they do we want those
      // to finish.
      if (!activeUploads.size) {
        this.stopEditingText();
      }

      if (message) {
        event.returnValue = Boolean(message);
        return message;
      }
    }
  };

  finishComposition = (event: React.SyntheticEvent) => {
    // Once the typing has finished after a composition event (either onKeyUp or
    // onMouseUp), we need to find out directly from the nativeEvent if the
    // composition has been resolved, we need this flag to preserve the native
    // selection once the IME editing is over.

    const nativeEvent = event && (event.nativeEvent as KeyboardEvent);
    const isStillComposing = nativeEvent ? nativeEvent.isComposing : false;

    if (!isStillComposing && this.compositionJustResolved) {
      this.compositionJustResolved = false;
      this.compositionFinished = true;
    } else if (this.compositionFinished) {
      this.compositionFinished = false;
    }
  };

  addDocMouseEvents() {
    const contentWindow = this.getWindow();
    if (!contentWindow) return;
    contentWindow.addEventListener('mouseup', this.onDocMouseUp);
    contentWindow.addEventListener('mousedown', this.onDocMouseDown);
    // Hide the image resize handles in Firefox.
    contentWindow.document.execCommand('enableObjectResizing', false, '');

    // Since the designer-canvas lives in an iframe the above ^ listeners will listen on that document,
    // we also want to add listeners on the parent document where the designer's sidebars/topbar/etc. live
    window.addEventListener('mouseup', this.onDocMouseUp);
    window.addEventListener('mousedown', this.onDocMouseDown);
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  removeDocMouseEvents() {
    const contentWindow = this.getWindow();
    if (!contentWindow) return;
    contentWindow.removeEventListener('mouseup', this.onDocMouseUp);
    contentWindow.removeEventListener('mousedown', this.onDocMouseDown);

    window.removeEventListener('mouseup', this.onDocMouseUp);
    window.removeEventListener('mousedown', this.onDocMouseDown);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  registerExitHandler() {
    this.unregisterExitHandler();
    this.unregisterContentEditorExitHandler = registerContentEditorExitHandler(
      () => this.safelyStopEditing({eventIsOnCanvas: false})
    );
  }

  unregisterExitHandler() {
    this.unregisterContentEditorExitHandler?.();
    this.unregisterContentEditorExitHandler = null;
  }

  shortcutBlurHack() {
    // Note: We need to wait for the next frame and then blur the active element on the top document
    // otherwise the site-iframe-next iframe will be focused and none of the mousetrap shortcuts in
    // ShortcutHelper will catch.
    window.requestAnimationFrame(() => {
      window.focus();
    });
  }

  // This makes sure that when e.preventDefault() is called in onKeyDown that it
  // blocks onKeyUp from firing.
  keyDownDefaultPrevented = false;
  preventDefaultInKeydown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    this.keyDownDefaultPrevented = true;
  };

  handleSpaceKeyDown = undoIfErrorCaught((event: React.KeyboardEvent) => {
    const editState = this.getEditState();
    if (!editState) {
      return;
    }

    const {subtree, selection, consecutiveSpacebarAttempts} = editState;

    const blockedSpacebarAttempts = (() => {
      const rangeSelection = assertRangeSelectionRecordType(selection);
      const prevChar =
        rangeSelection && getCharBeforeCaret(subtree, rangeSelection);
      // We preventDefault() and return on all our funky space handling as keyboardShortcuts and textShortcuts above.
      // In cases where a regular space is allowed, we let it fall through to the onBeforeInput handler
      // to insert it so we can allowNativeRender and keep typing performant
      const blockedSpacebarPress = prevChar === ' ';
      // Increment blockedSpacebarAttempts here so we can send it
      // to multiple stores (including notificationStore to show a warning when we reach a certain count)
      if (blockedSpacebarPress) {
        this.preventDefaultInKeydown(event);
        return consecutiveSpacebarAttempts + 1;
      } else {
        return consecutiveSpacebarAttempts;
      }
    })();

    this.dispatch(
      keyDown({
        key: SPACE,
        seedId: generateGuid(),
        consecutiveSpacebarAttempts: blockedSpacebarAttempts,
      })
    );
  }, this.dispatch);

  handleKeyboardShortcut(keyboardShortcut: ShortcutsType) {
    const editState = this.getEditState();
    if (!editState) {
      return;
    }

    const {allowNativeRender, selection} = editState;
    if (
      keyboardShortcut === SHORTCUTS.UNDO ||
      keyboardShortcut === SHORTCUTS.REDO
    ) {
      const action = keyboardShortcut === SHORTCUTS.UNDO ? undo() : redo();
      if (allowNativeRender) {
        // Note: If we were allowing native render before undoing, then React's
        // understanding of the state of the native tree is out of sync with reality.
        // In order to bring it back in sync we force the render cycle here so it can recalculate
        // the current state of the world so that when the undo state is swapped in in the next
        // step it can properly calculate and render the diff between the current state and the undo state
        // and apply those changes to the native dom
        this.isSynchronizingNativeRenderBaseline = true;
        this.forceUpdate(() => {
          this.isSynchronizingNativeRenderBaseline = false;
          this.dispatch(action);
        });
      } else {
        return this.dispatch(action);
      }
    } else if (selection.type !== 'Node') {
      this.dispatch(
        keyboardShortcutPressed({seedId: generateGuid(), keyboardShortcut})
      );
    }
  }

  // Tab/Shift+Tab moves the store selection between the field's top-level
  // blocks (component -> Node selection, text -> caret). Returns false at the
  // boundaries or when the field has no components, so the caller stops
  // editing and native Tab exits to the adjacent field.
  tryComponentTabNavigation(
    event: React.KeyboardEvent,
    editState: ContentEditorActive
  ): boolean {
    if (event.which !== TAB || event.metaKey || event.ctrlKey) return false;

    const nativeSubtree = this.nativeSubtree;
    if (!nativeSubtree) return false;

    // Only in the Data Manager (editTargetFieldIdPath is set there); on the
    // canvas Tab keeps its native behavior.
    if (!isNativeIdPath(this.props.editTargetFieldIdPath)) {
      return false;
    }

    const {subtree, selection} = editState;
    const adapter = this.props.contentEditorViewportAdapter;

    const blocks = Array.from(nativeSubtree.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement
    );
    if (blocks.length === 0) return false;

    const isComponentBlock = (el: HTMLElement) =>
      el.hasAttribute(WEBFLOW_COMPONENT_ROOT_ATTR);

    // Plain rich text keeps native Tab.
    if (!blocks.some(isComponentBlock)) return false;

    // A caret inside a list keeps Tab/Shift+Tab for indent/outdent (see
    // getKeyboardShortcut), so don't traverse blocks in that case.
    const rangeSelection = assertRangeSelectionRecordType(selection);
    if (
      rangeSelection &&
      getClosestListElement(EElement(subtree), rangeSelection.anchorNodeId)
    ) {
      return false;
    }

    let currentIndex = -1;
    const nodeSelection = assertNodeSelectionRecordType(selection);
    if (nodeSelection?.nodeId) {
      currentIndex = blocks.findIndex(
        (el) =>
          getNodeIdFromNativeNode({subtree, nativeNode: el}) ===
          nodeSelection.nodeId
      );
    } else {
      // Caret in text: climb from the DOM anchor to its top-level block.
      const anchorNode = this.getWindow()?.getSelection()?.anchorNode ?? null;
      if (anchorNode && nativeSubtree.contains(anchorNode)) {
        let node: Node | null = anchorNode;
        while (node && node.parentNode !== nativeSubtree) {
          node = node.parentNode;
        }
        if (node instanceof HTMLElement) currentIndex = blocks.indexOf(node);
      }
    }

    const forward = !event.shiftKey;
    const targetIndex =
      currentIndex === -1
        ? forward
          ? 0
          : -1
        : currentIndex + (forward ? 1 : -1);

    // At a boundary: let the caller stop editing so native Tab exits.
    if (targetIndex < 0 || targetIndex >= blocks.length) {
      return false;
    }

    const targetBlock = blocks[targetIndex]!;
    let parsedSelection: ParsedSelectionType | null | undefined;
    if (isComponentBlock(targetBlock)) {
      parsedSelection = parseSelectionFromClickEvent(
        subtree,
        {target: targetBlock, clientX: 0, clientY: 0},
        nativeSubtree,
        adapter
      );
    } else {
      const blockId = getNodeIdFromNativeNode({
        subtree,
        nativeNode: targetBlock,
      });
      parsedSelection = blockId
        ? moveCaretToDeepStartOfNode(blockId)(subtree)
        : null;
    }
    if (!parsedSelection) return false;

    this.preventDefaultInKeydown(event);
    this.dispatch(mouseDown({seedId: generateGuid(), parsedSelection}));
    return true;
  }

  onKeyDown = undoIfErrorCaught((event: React.KeyboardEvent) => {
    const editState = this.getEditState();
    if (!editState) {
      return;
    }

    // Component Tab navigation is store-driven, not focus-driven: restarting
    // edit mode remounts the placeholder buttons and races the focus.
    if (this.tryComponentTabNavigation(event, editState)) {
      return;
    }

    if (this.latencyProfiler && shouldProfileTextInputLatency(editState)) {
      this.latencyProfiler.setTags({
        editContext: getTextInputEditContext(editState),
      });
      this.latencyProfiler.start(event);
    }

    const {selection} = editState;
    const keyboardShortcut = getKeyboardShortcut(editState, event);

    const rangeSelection = assertRangeSelectionRecordType(selection);

    if (editState && this.nativeSubtree) {
      const textShortcut = getTextShortcut(editState, event);
      const key = event.which;

      if (
        keyboardShortcut === SHORTCUTS.COPY ||
        keyboardShortcut === SHORTCUTS.CUT
      ) {
        return;
      } else if (keyboardShortcut === SHORTCUTS.PASTE) {
        if (
          isPlainTextPasteShortcut(event) &&
          typeof this.getClipboard()?.readText === 'function'
        ) {
          this.preventDefaultInKeydown(event);
          this.pastePlainTextFromClipboard(editState).catch(() => undefined);
        }
        // Note: This is here because we specifically want
        // to allow the native paste event to happen.
        // SHORTCUTS.PASTE exists to prevent reaching the condition
        // below that handles nodeSelection case
        return;
      } else if (
        keyboardShortcut === SHORTCUTS.STOP_EDITING &&
        key === ESC &&
        editState.activeToolbar === TOOLBARS.COMPONENTSETTINGS
      ) {
        // if the component settings toolbar is open (only exists in data manager and mini-DM)
        // and we press escape, go back to the component toolbar (wrench icon, component selected)
        // and do not close the data manager.
        this.preventDefaultInKeydown(event);
        this.dispatch(contentEditorMenuToggled({menu: TOOLBARS.COMPONENT}));
      } else if (keyboardShortcut === SHORTCUTS.STOP_EDITING) {
        const stoppedEditing = this.safelyStopEditing();
        if (key !== TAB || !stoppedEditing) {
          // In the data-manager we need the native behavior of tab and shift+tab
          // to move between rich text fields. In cases where we can't stop
          // editing safely (eg: there are uploads in progress) when the user hit
          // tab then we do want to preventDefault and block the tab. For other
          // STOP_EDITING shortcuts like ESC we always want to preventDefault
          // whether we stopped or not.
        }
      } else if (keyboardShortcut === SHORTCUTS.SELECT_ALL) {
        const parsedSelection = this.getParsedNativeSelection();
        if (parsedSelection) {
          this.dispatch(selectionUpdated({parsedSelection}));
        }
      } else if (keyboardShortcut) {
        this.preventDefaultInKeydown(event);
        this.handleKeyboardShortcut(keyboardShortcut);
      } else if (rangeSelection && textShortcut) {
        // Text shortcuts like `- ` at the beginning of a paragraph to start a list, or SPACE before a space to treat
        // it like a right arrow.
        this.dispatch(
          textShortcutActivated({seedId: generateGuid(), textShortcut})
        );
        this.preventDefaultInKeydown(event);
      } else if (editState.activeToolbar === TOOLBARS.COMPONENTSETTINGS) {
        // when the component settings toolbar is open, we don't want the content editor
        // to react to keypresses in the component settings toolbar
        this.preventDefaultInKeydown(event);
        return;
      } else if (
        (event.metaKey || event.ctrlKey) &&
        key === ENTER &&
        editState.activeToolbar === TOOLBARS.COMPONENT &&
        isNativeIdPath(this.props.editTargetFieldIdPath)
      ) {
        // Cmd/Ctrl+Enter opens the selected component's settings; plain Enter
        // falls through below to insert a line after it. Clear
        // keyDownDefaultPrevented (rather than calling preventDefaultInKeydown)
        // because focus moves into the panel — no keyUp comes back to clear it,
        // and a stale flag would make the next Escape's keyUp stop editing and
        // exit the field instead of returning to the component. Gated to the
        // Data Manager (like Tab); the toolbar/panel don't render on canvas, so
        // opening them there would trap keys with no visible UI.
        event.preventDefault();
        this.keyDownDefaultPrevented = false;
        this.dispatch(openComponentSettings());
      } else if (key === ENTER || key === BACKSPACE || key === DELETE) {
        if (key === ENTER && this.props.isEditingSelectedNodeInInstance) {
          const {plugins, components, environmentContext} =
            this.props.renderProps;

          // If the field we are editing is not multiline we block the ENTER
          if (
            !isEditingMultiLineNodeInInstance(
              environmentContext,
              components,
              plugins
            )
          ) {
            this.preventDefaultInKeydown(event);
            return;
          }
        }
        // `editTargetFieldIdPath` is set when in the Data Manager or Data
        // Manager Modal. Match the inline convention used by the paste
        // handler so callers see the same `isInDataManagerContext` name.
        const isInDataManagerContext = isNativeIdPath(
          this.props.editTargetFieldIdPath
        );
        if (
          (key === BACKSPACE || key === DELETE) &&
          !event.altKey &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.shiftKey &&
          canAllowNativeDeletion(
            editState,
            key,
            this.getWindow()?.getSelection(),
            isInDataManagerContext
          )
        ) {
          return;
        }
        this.preventDefaultInKeydown(event);
        if (key === BACKSPACE || key === DELETE) {
          this.dispatchAfterNativeRenderSync(
            keyDown({key, seedId: generateGuid()})
          );
        } else {
          this.dispatch(keyDown({key, seedId: generateGuid()}));
        }
      } else if (rangeSelection && key === SPACE) {
        this.handleSpaceKeyDown(event);
      } else if (selection.type === 'Node') {
        // When a figure is selected the caret is not in the dom,
        // so arrow keys will make the canvas scroll, we want to prevent that bigly
        // We handle moving the caret away from the figure in the onKeyUp handler in the store
        this.preventDefaultInKeydown(event);
        if (isArrow(key)) {
          this.dispatch(keyDown({key, seedId: generateGuid()}));
        }
      } else if (
        event.key === 'ArrowDown' &&
        editState.activeToolbar === CONTENT_EDITOR_MENUS.ADD_MENU
      ) {
        this.preventDefaultInKeydown(event);
        this.dispatch(contentEditorAddMenuFocusSet({setFocus: true}));
      }

      // Preemptively hide the caret when the adjacent element is
      // node-selectable so it doesn't flash in the wrong position if the
      // browser skips over it. onKeyUp restores it if no skip occurred.
      if (rangeSelection && isArrow(key)) {
        const direction: 'left' | 'right' =
          key === RIGHT || key === DOWN ? 'right' : 'left';
        const targetElement = getTargetElement(direction)(editState);
        if (targetElement && isHiddenFromContentEditable(targetElement)) {
          this.nativeSubtree.style.caretColor = 'transparent';
        }
      }
    }
  }, this.dispatch);

  // Handle IME events
  // Ref: https://w3c.github.io/uievents/tools/key-event-viewer.html
  onCompositionStart = undoIfErrorCaught(() => {
    this.isComposing = true;
  }, this.dispatch);

  onCompositionEnd = undoIfErrorCaught(() => {
    // Once the composition officially ends on korean input, we need to add a
    // millisecond delay to allow `compositionStart` to fire again because the
    // editor might be trying to render too fast and some composed characters
    // are resolved before the user actually stops composing
    window.requestAnimationFrame(() => {
      if (this.isComposing && !this.compositionFinished) {
        this.isComposing = false;
        this.compositionJustResolved = true;
      }
    });
  }, this.dispatch);

  onKeyUp = undoIfErrorCaught(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const editState = this.getEditState();
      const key = event.which;
      const metaKey = isMetaKey(event);
      const markdownKey = getMarkdownKey(event);

      if (key === ESC && this.keyDownDefaultPrevented) {
        this.safelyStopEditing();
      } else if (editState && markdownKey !== null) {
        // During native typing, props are stale (SCU returned false) so
        // the store's subtree text is outdated. Read from the native DOM
        // when available so `**bold**`, `_italic_`, `` `code` `` detect
        // against the text the user is actually looking at. Fall back to
        // the store-based matcher when there's no anchor text.
        //
        // Mirror the guards from hasMatchingMarkdownInCurrentNode so the
        // DOM path doesn't trigger markdown formatting in non-RichText
        // contexts (plain text fields, etc.) or when no caret is in a
        // text node — handleMarkdownFormatting expects a RichText subtree
        // and a range selection.
        const hasMarkdown = (() => {
          if (!isRichText(editState.subtree)) return false;
          if (!assertRangeSelectionRecordType(editState.selection)) {
            return false;
          }
          const nativeText = getNativeAnchorText(this.getWindow());
          if (nativeText) {
            return getInlineMarkdownTypeInText(nativeText) === markdownKey;
          }
          return hasMatchingMarkdownInCurrentNode({
            markdownKey,
            state: editState,
          });
        })();
        if (hasMarkdown) {
          this.dispatch(
            markdownFormattingActivated({seedId: generateGuid(), key})
          );
        }
      } else if (
        editState &&
        event.key === '/' &&
        isRichText(editState.subtree)
      ) {
        this.dispatch(slashCommandActivated());
      }

      if (this.keyDownDefaultPrevented) {
        event.preventDefault();
        this.keyDownDefaultPrevented = false;
        return;
      }
      this.refineNativeSelection();

      let parsedSelection = this.getParsedNativeSelection();

      this.finishComposition(event);

      // Shadow DOM elements (e.g. code components) have no valid caret
      // position, so the browser skips over them during arrow key navigation.
      // Detect when that happens and correct the selection to land on the
      // skipped element instead.
      if (parsedSelection && editState && this.nativeSubtree) {
        parsedSelection = getSelectionCorrectedForSkippedNode(
          parsedSelection,
          editState,
          this.nativeSubtree,
          key,
          this.props.contentEditorViewportAdapter
        );

        // Restore the caret if onKeyDown hid it preemptively but no skip
        // occurred (the caret stayed in the same element).
        if (parsedSelection.type !== 'Node') {
          this.nativeSubtree.style.caretColor = '';
        }
      }

      if (parsedSelection) {
        this.dispatch(
          keyUp({key, metaKey, parsedSelection, guid: generateGuid()})
        );
      }
    },
    this.dispatch
  );

  onBeforeInput = undoIfErrorCaught(
    (event: React.CompositionEvent<HTMLInputElement>) => {
      const editState = this.getEditState();
      if (editState) {
        const chars = event.data;

        const activeContentEditor = getContentEditorActive(editState);

        if (
          activeContentEditor &&
          isNumberEditing(activeContentEditor) &&
          chars
        ) {
          // For Number fields, only allow numerical input
          // Includes comma for locales that use comma as decimal separator
          const isValidNumberInput = VALID_NUMBER_CHARS.has(chars);

          if (!isValidNumberInput) {
            event.preventDefault();
            return;
          }
        }

        // Native render improves performances. We only allow it in a small set of
        // circumstances when:
        // A) we can guarantee that what the browser would natively do will match
        //    the subtree. usually only when inserting characters with some
        //    limitations
        // B) the user does something natively that we want to capture into the
        //    store after the fact. like using spellcheck to correct a spelling or
        //    holding down a letter to add an accent.
        const reactRender = !canAllowNativeRender(editState);

        if (reactRender) {
          event.preventDefault();

          // If we reached this point we're just inserting characters into a text node
          this.dispatch(charsInserted({seedId: generateGuid(), chars}));
        }

        // If allowing nativeRender, then we don't preventDefault here which lets
        // onInput replace the value of the subtree's textNode with the one from the
        // native textNode, and optionally update the selection as well to match the
        // native one depending on the input type.
        //
        // This allows the user to add special characters eg: by holding the letter
        // e on osx and choosing the accent, or using spell check to replace a
        // misspelled word.
      }
    },
    this.dispatch
  );

  onInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const editState = this.getEditState();
    if (!editState) {
      return;
    }

    const {subtree} = editState;

    const win = this.getWindow();
    if (!win) return;
    const nativeSelection = win.getSelection();
    if (!nativeSelection) return;
    const nativeAnchorNode = nativeSelection.anchorNode;
    if (!nativeAnchorNode) return;
    const nodeId = getNodeIdFromNativeNode({
      subtree,
      nativeNode: nativeAnchorNode,
    });
    const el = nodeId
      ? Expressions.getElementById(EElement(subtree), nodeId)
      : null;

    if (
      'wholeText' in nativeAnchorNode &&
      'nodeValue' in nativeAnchorNode &&
      nativeAnchorNode.nodeValue !== nativeAnchorNode.wholeText
    ) {
      // This means we're inside a textNode that has adjacent textNodes. This
      // should never ever be allowed and can lead to buggy behavior.
      console.warn(
        'AdjacentTextNodesEncountered',
        'ContentEditor edits occurred within content containing adjacent text nodes, which can prevent content from being updated.',
        {
          nativeAnchorNodeId: nodeId,
        }
      );

      // Track the error rate, excluding false reports caused by line breaks
      // Only report once per site to save on telemetry costs
      if (editState && nodeId) {
        const parent = Expressions.getParent(EElement(subtree), nodeId);
        if (parent) {
          const children = getChildren(parent);
          const hasLinebreak = children?.some(isLineBreakElement) ?? false;

          if (!hasLinebreak && !adjacentTextNodesLogged) {
            adjacentTextNodesLogged = true;

            const siteId = this.props.site?.get('id') || 'unknown';

            const telemetryData = {
              siteId,
              isCmsBound: isEditingCMSOnCanvas(editState),
              isRichText: isRichText(editState.subtree),
              parentIsFormat: isFormatElement(parent),
            };

            telemetry.sendEvent(
              'datadog::adjacentTextNodesEncountered',
              telemetryData
            );
          }
        }
      }

      return;
    }

    if (nativeSelection.isCollapsed && el && isStringElement(el)) {
      const nativeValue = nativeAnchorNode.nodeValue;

      if (nativeValue !== Expressions.getText(el.data)) {
        const nativeEvent = event.nativeEvent as InputEvent;
        const allowCmsBoundRichTextNativeRender =
          this.props.runtimeEnvironment === WebflowEnvironments.DESIGNER &&
          Boolean(this.props.getContentEditorLiveSnapshot) &&
          nativeEvent.inputType === 'insertText' &&
          !nativeEvent.isComposing &&
          !this.isComposing &&
          !this.compositionJustResolved;

        if (
          nativeValue !== null &&
          nativeEvent.inputType === 'insertReplacementText'
        ) {
          // This happens after spell check, don't modify the selection the
          // one we had in state was more accurate.
          this.dispatch(
            textValueReplaced({
              textNodeId: el.id,
              value: nativeValue,
            })
          );
        } else {
          // Everything else, This includes regular typing, as well as holding
          // e to add an accent caused the values to mismatch so use the
          // native selection
          const shouldKeepNativeRender = shouldKeepNativeRenderForTyping(
            editState,
            {allowCmsBoundRichTextNativeRender}
          );
          const parsedSelection = this.getParsedNativeSelection();
          if (nativeValue !== null && parsedSelection) {
            this.dispatch(
              textValueReplaced({
                textNodeId: el.id,
                value: nativeValue,
                parsedSelection,
                allowCmsBoundRichTextNativeRender,
              })
            );
          }

          // Latency profiler bookkeeping: the SCU only skips the React
          // reconcile when the reducer below sets allowNativeRender=true,
          // which is exactly shouldKeepNativeRenderForTyping. Use it as
          // the single source of truth.
          //   - Native path: finish() here (componentDidUpdate won't fire
          //     so it's the only place the keystroke can be measured).
          //   - React path (input outside the native-render guards): leave
          //     finish() to componentDidUpdate as before so the metric measures
          //     keyDown -> reconcile complete and pre/post comparisons
          //     stay apples-to-apples.
          if (this.latencyProfiler && shouldKeepNativeRender) {
            this.latencyProfiler.finish();
          }
        }
      }
    }
  };

  onMouseDown = undoIfErrorCaught((event: React.MouseEvent) => {
    const editState = this.getEditState();
    if (!editState || !this.nativeSubtree) {
      return;
    }

    const {subtree} = editState;
    const nativeEvent: MouseEvent = event.nativeEvent;
    // We get the selection from the click event here because the caret won't be
    // placed until after this handler executes in most cases. Right click is a
    // special case handled  below.
    const parsedSelectionFromEvent = parseSelectionFromClickEvent(
      subtree,
      nativeEvent,
      this.nativeSubtree,
      this.props.contentEditorViewportAdapter
    );
    const isRightClick = nativeEvent.button === 2;

    if (isRightClick) {
      // rightClicking has special native behavior around spellcheck (it'll
      // select a word) so we need to look at the native selection and update
      // the store.
      const parsedSelection = this.getParsedNativeSelection();
      // If right clicking on a figure we want to update the store selection
      // from the click event and not look at the native selection or it'll be
      // slightly borked
      if (parsedSelection && parsedSelection.type !== 'Node') {
        this.dispatch(mouseDown({seedId: generateGuid(), parsedSelection}));
        return;
      }
    }
    if (parsedSelectionFromEvent) {
      this.dispatch(
        mouseDown({
          seedId: generateGuid(),
          parsedSelection: parsedSelectionFromEvent,
        })
      );
    }
  }, this.dispatch);

  // Selecting a component root (and showing its settings mini-toolbar) is
  // otherwise only driven by onMouseDown, so Tab-focusing between component
  // roots moves native DOM focus without ever selecting the instance. Mirror
  // onMouseDown's component-root selection here so keyboard users can reach
  // the settings toolbar too.
  onComponentRootFocus = undoIfErrorCaught((event: React.FocusEvent) => {
    const editState = this.getEditState();
    if (!editState || !this.nativeSubtree) {
      return;
    }

    const target = event.target as Element;
    if (!target?.hasAttribute?.(WEBFLOW_COMPONENT_ROOT_ATTR)) {
      return;
    }

    const {subtree, selection} = editState;
    const parsedSelectionFromEvent = parseSelectionFromClickEvent(
      subtree,
      {target, clientX: 0, clientY: 0},
      this.nativeSubtree,
      this.props.contentEditorViewportAdapter
    );

    if (!parsedSelectionFromEvent) {
      return;
    }

    // A click on the same instance already dispatched this via onMouseDown —
    // skip the redundant dispatch when focus follows a click.
    const focusedNodeId = assertNodeSelectionType(
      parsedSelectionFromEvent
    )?.nodeId;
    const selectedNodeId = assertNodeSelectionRecordType(selection)?.nodeId;
    if (focusedNodeId && focusedNodeId === selectedNodeId) {
      return;
    }

    this.dispatch(
      mouseDown({
        seedId: generateGuid(),
        parsedSelection: parsedSelectionFromEvent,
      })
    );
  }, this.dispatch);

  onClick = (event: React.MouseEvent) => {
    // While editing a form label with the ContentEditor, clicking on the label
    // will try to focus the input that the label is for. This is here to prevent
    // that.

    // Since it has no adverse affect on the ContentEditor and to help surface
    // bugs in this hack we just apply it to all textHolders. If we were to
    // limit it to only form labels we'd only realize the hack was broken if we
    // remember to test labels every time we upgrade React or make an unrelated
    // change that could break it.
    event.preventDefault();
  };

  onDoubleClick = undoIfErrorCaught((event: React.MouseEvent) => {
    const editState = this.getEditState();
    if (!editState || !this.nativeSubtree) {
      return;
    }

    const {subtree} = editState;
    if (subtree === null) {
      return;
    }

    const nativeTarget = event.target as Node;
    const nodeId = getNativeIdFromNativeNode({
      subtree,
      nativeNode: nativeTarget,
    });

    if (nodeId) {
      const guid = getGuidNullable(nodeId);
      if (!guid) return;

      const element = Expressions.getElementById(EElement(subtree), guid);

      if (element && isUserComponent(element.type)) {
        if (
          isEditingCMSOnCanvas(editState) ||
          isEditingRichTextComponentProp(editState)
        ) {
          return;
        }

        const {components} = this.props.renderProps;
        if (isCodeComponent(components)(element.type)) {
          return;
        }

        const stopEditingTextProp =
          this.props.renderProps.environmentContext.get('stopEditingText');

        if (typeof stopEditingTextProp === 'function') {
          stopEditingTextProp(nodeId);
        }
      }
    }
  }, this.dispatch);

  onMouseUp = undoIfErrorCaught((event: React.MouseEvent) => {
    const editState = this.getEditState();
    if (!editState) {
      return;
    }

    const {selection} = editState;

    const nodeSelection = assertNodeSelectionRecordType(selection);

    // selection for figures is already computed in onMouseDown
    if (!nodeSelection) {
      this.refineNativeSelection();
      const parsedSelection = this.getParsedNativeSelection();
      if (parsedSelection) {
        this.dispatch(mouseUp({parsedSelection}));
      }
    }
    this.finishComposition(event);
  }, this.dispatch);

  pastePlainTextFromClipboard = async (editState: ContentEditorActive) => {
    const clipboard = this.getClipboard();
    if (typeof clipboard?.readText !== 'function') {
      return;
    }

    try {
      const clipboardText = await clipboard.readText();
      if (!clipboardText) {
        return;
      }

      const {plugins, components, environmentContext} = this.props.renderProps;
      const isMultiLine =
        this.props.isEditingSelectedNodeInInstance &&
        isEditingMultiLineNodeInInstance(
          environmentContext,
          components,
          plugins
        );
      const {subtree, selection} = handlePaste(
        generateGuid(),
        createPlainTextClipboardEvent(clipboardText),
        editState,
        components,
        plugins,
        this.dispatch,
        this.props.isEditingSelectedNodeInInstance,
        isMultiLine
      );

      this.dispatch(paste({subtree, selection}));
    } catch {
      // If clipboard read permission is denied, the Windows shortcut remains a no-op.
    }
  };

  onPaste = (event: React.ClipboardEvent) => {
    const editState = this.getEditState();
    if (!editState) {
      return;
    }

    event.preventDefault();
    const {plugins, components, environmentContext} = this.props.renderProps;
    let isMultiLine = false;
    if (this.props.isEditingSelectedNodeInInstance) {
      isMultiLine = isEditingMultiLineNodeInInstance(
        environmentContext,
        components,
        plugins
      );
    }

    const isEditMode =
      environmentContext.get('editSiteMode') &&
      !environmentContext.get('buildMode');

    const shouldStripComponents = shouldStripPastedComponents(
      editState,
      isEditMode
    );
    const focusedComponentType = environmentContext.get(
      'focusedComponentType'
    ) as Plugins.QualifiedElement | undefined;
    const isEditingRTInsideComponentDef =
      isRichText(editState.subtree) &&
      focusedComponentType != null &&
      isUserComponent(focusedComponentType);
    const isEditingRichTextProp =
      isEditingRichTextComponentPropOverride(editState);
    const isEditingInRichTextModal = Boolean(editState.fromModal);

    // Block paste entirely if clipboard contains a component and we're in a context where
    // components are not allowed. e.g. pasting into a component definition in a Rich Text Element,
    // Rich Text Property Default Value, on-canvas override, etc.
    if (
      isEditingRTInsideComponentDef ||
      isEditingInRichTextModal ||
      isEditingRichTextProp
    ) {
      const jsonData = event.clipboardData.getData('application/json');
      if (jsonData) {
        try {
          const clipboardJson = JSON.parse(jsonData);
          const hasComponent =
            Array.isArray(clipboardJson) &&
            clipboardJson.some(
              (node: {type?: string}) => node.type === 'Symbol'
            );
          if (hasComponent) {
            this.dispatch(
              contentEditorComponentValidationFailed({
                errorMessage:
                  'Components cannot be pasted into Rich Text in this context.',
                reason: isEditingRTInsideComponentDef
                  ? 'inside component definition'
                  : 'rich text property override',
              })
            );
            return;
          }
        } catch {
          // If JSON parse fails, fall through to normal paste
        }
      }
    }

    const {subtree, selection} = handlePaste(
      generateGuid(),
      event,
      editState,
      components,
      plugins,
      this.dispatch,
      this.props.isEditingSelectedNodeInInstance,
      isMultiLine,
      shouldStripComponents
    );

    const siteOrDatabaseId = this.getSiteOrDatabaseId();

    this.dispatch(paste({subtree, selection}));

    const editTargetFieldIdPath = this.props.editTargetFieldIdPath;

    // `editTargetFieldIdPath` is set when in the Data Manager or Data Manager Modal.
    const isInDataManagerContext = isNativeIdPath(editTargetFieldIdPath);

    // Check if we're editing CMS content on canvas (both Designer and Legacy Editor)
    const isEditingCMSOnDesignerCanvas = isEditingCMSOnCanvas(editState);

    // Skip image importing in Data Manager or when editing CMS-bound content on canvas.
    // These contexts have their own image handling workflows that don't require the standard import process.
    if (isInDataManagerContext || isEditingCMSOnDesignerCanvas) return;

    const importOpts = {
      siteId: siteOrDatabaseId,
      databaseId: undefined,
      fileId: undefined,
      figmaNodeId: undefined,
      figmaFileKey: undefined,
      figmaFileName: undefined,
      origFigmaFileName: undefined,
      figmaScale: undefined,
    };

    findRemoteImages(siteOrDatabaseId, subtree).forEach((imageFigureNode) => {
      const image = findElementDescendant(imageFigureNode, isImageElement);

      if (!image) return;

      const urlExpr = Expressions.getIn(image.data, [
        {in: 'Record', at: 'attr'},
        {in: 'Record', at: 'src'},
      ]);

      if (!urlExpr) return;

      const urlValue = Expressions.getText(urlExpr);

      const imageFigureId = imageFigureNode.id;

      this.dispatch(imageImportStarted({imageFigureId}));

      importFile(urlValue, importOpts)
        .then((jsonFile) => {
          this.dispatch(imageImportFinished({jsonFile, imageFigureId}));
        })
        .catch(() => {
          this.dispatch(imageUploadedWithError({imageFigureId}));

          setTimeout(() => {
            this.dispatch(imageImportFailed({imageFigureId}));
          }, 1000);
        });
    });
  };

  handleInstanceNodeEditingEnd = (
    editState: ContentEditorActive,
    selectedNativeIdPath: NativeId[] | null | undefined,
    isEditingCms: boolean
  ) => {
    const nodeInInstanceElementRoot = selectedNativeIdPath
      ? getInnermostInstanceRootElement(
          selectedNativeIdPath,
          this.props.renderProps.components
        )
      : null;

    const isEditingTypographicRootElement =
      nodeInInstanceElementRoot &&
      (isTypographic(nodeInInstanceElementRoot) ||
        isRichText(nodeInInstanceElementRoot));

    if (isEditingTypographicRootElement) {
      const nativeNode = selectedNativeIdPath
        ? getNativeNodeByNativeIdPath(selectedNativeIdPath)
        : null;

      if (
        nativeNode &&
        selectedNativeIdPath &&
        selectedNativeIdPath.length >= 2
      ) {
        const rootElementId =
          selectedNativeIdPath[selectedNativeIdPath.length - 2];
        if (rootElementId) {
          removeInstanceInFavorOfWIdAttr(
            nativeNode,
            // we want to use the root element id since it's the original value for this attribute
            rootElementId
          );
        }
      }
    }

    // We trigger field update only if the content editor is "dirty" and some update has been performed.
    // The Content Editor undoStack is expected to have a size of at least 1, with a larger size indicating
    // that some change has occured
    const shouldTriggerFieldUpdate = editState.undoStack.size > 1;

    const shouldAutoSave = !editState.isAutoSaveDisabled;

    // If we're editing a CMS-bound value on the canvas, we do not need to change the override field as we have already updated the cms field.
    if (shouldTriggerFieldUpdate && !isEditingCms && shouldAutoSave) {
      const {subtree} = editState;
      const {plugins, components, environmentContext} = this.props.renderProps;
      const editTargetNativeIdPath: NativeId[] | null | undefined =
        environmentContext.get('editTargetNativeIdPath');

      const focusedSymbolNativeIdPath: string[] = environmentContext.get(
        'focusedSymbolNativeIdPath'
      );
      if (
        !selectedNativeIdPath ||
        !focusedSymbolNativeIdPath ||
        !editTargetNativeIdPath
      ) {
        return;
      }

      updateOverrideFieldValue({
        targetElementId: first(
          getRelativePath(editTargetNativeIdPath, focusedSymbolNativeIdPath)
        ) as string,
        selectedNativeIdPath,
        focusedSymbolNativeIdPath,
        subtree,
        components,
        plugins,
        dispatch: this.dispatch,
      });
      return;
    }
    return this.dispatch(textOfNodeSelectedInInstanceEditingEnded());
  };

  safelyStopEditing = async (options?: {eventIsOnCanvas: boolean}) => {
    const eventIsOnCanvas = options?.eventIsOnCanvas;
    let editState = this.getEditState();
    let contentEditorValidationError =
      this.props.renderProps.environmentContext.get(
        'contentEditorValidationError'
      );

    // Blur the native subtree element if it exists
    if (this.nativeSubtree) {
      this.nativeSubtree.blur();
    }

    // Flux commits synchronously while React may not reconcile native CMS
    // typing, so an immediate exit can overtake committed props. Prefer the
    // live store snapshot when it belongs to the same item/field, and fall
    // back to props otherwise (inactive/mismatched snapshots, non-CMS and
    // Data Manager paths which carry no item/field).
    if (editState?.item && editState?.field && !editState.isAutoSaveDisabled) {
      const liveSnapshot = this.getLiveEditState();
      if (
        liveSnapshot &&
        liveSnapshot.contentEditorState.item?.get('_id') ===
          editState.item.get('_id') &&
        liveSnapshot.contentEditorState.field?.get('id') ===
          editState.field.get('id')
      ) {
        editState = liveSnapshot.contentEditorState;
        contentEditorValidationError =
          liveSnapshot.contentEditorValidationError;
      }
    }

    if (!editState) {
      return true;
    }

    if (this.inFlightCmsSave) {
      return this.inFlightCmsSave;
    }

    const {item, field, itemFieldIsSaving} = editState;

    // Blocking exit protects an unsaved value from being dropped. A session
    // that never writes has no such value, and the error can predate it, so
    // blocking would only trap the user in a session they cannot leave.
    const shouldBlockExitOnValidationError = !editState.isAutoSaveDisabled;

    contentEditorValidationError =
      shouldBlockExitOnValidationError && contentEditorValidationError;

    if (contentEditorValidationError && !editState.confirmDiscardIsOpen) {
      const showValidationError = () => {
        this.dispatch(
          contentEditorValidationErrorShown({
            error: contentEditorValidationError,
          })
        );
      };

      // show modal if toast has already been shown OR show straight away if the click was outside the canvas.
      if (eventIsOnCanvas === false || editState.validationErrorShown) {
        this.dispatch(contentEditorModalOpened());

        if (!editState.validationErrorShown) {
          showValidationError();
        }
        const didDiscard = await confirmDiscard({
          onSubmit: this.stopEditingText,
          onCancel: () => {
            editState.validationErrorShown && showValidationError();
          },
        });
        this.applySelection();
        this.dispatch(contentEditorModalClosed());
        return didDiscard;
      } else {
        showValidationError();
      }

      // return early to prevent editing and unfocusing if there is an validation error
      return false;
    }

    if (contentEditorValidationError) return false;

    const selectedNativeIdPath = this.props.renderProps.environmentContext.get(
      'selectedNativeIdPath'
    );

    const isEditingCms = item && field;

    // If a CMS on-canvas save is currently in progress, return early and not go through save again
    if (itemFieldIsSaving) {
      return false;
    }

    // If we're editing a CMS-bound value on the canvas, we need to save the new value in
    // the CMS item.
    const shouldAutoSave = !editState.isAutoSaveDisabled;
    if (isEditingCms && shouldAutoSave) {
      const shouldAttributeToAi = editState.aiData.pendingCmsAiAttribution;

      // Start reporting latency for boundCanvasTextSavingLatency event.
      ClientLatencyTools.start(
        TelemetryEventLabel.BOUND_CANVAS_TEXT_SAVING_LATENCY
      );
      const isPrimaryLocale = isActiveLocalePrimary(
        this.props.renderProps.pluginsContext
      );

      const {plugins, components, environmentContext} = this.props.renderProps;
      const saveCollectionItemField = environmentContext.get(
        'saveCollectionItemField'
      );

      const collectionId = item.get('_cid');
      const fieldSlug = field.get('slug');
      const fieldType = field.get('type');
      const originalValue = item.get(fieldSlug, '');
      const fieldValidations = field.get('validations');

      // For RichText fields, originalValue is an Immutable List/Map that needs .toJS()
      // For other field types, pass it directly (string, number, null, undefined)
      const originalFieldValue =
        fieldType === 'RichText'
          ? originalValue && typeof originalValue.toJS === 'function'
            ? originalValue.toJS()
            : originalValue === null
              ? []
              : originalValue
          : (originalValue ?? '');

      const updatedValue = getUpdatedItemFieldValue({
        fieldType,
        originalFieldValue,
        seedId: generateGuid(),
        state: editState,
        plugins,
        components,
        fieldValidations: fieldValidations
          ? fieldValidations.toJS()
          : undefined,
      });
      if (!updatedValue) {
        // If no values changed, report finished latency for boundCanvasTextSavingLatency event.
        ClientLatencyTools.finishAndReport(
          TelemetryEventLabel.BOUND_CANVAS_TEXT_SAVING_LATENCY,
          {
            fieldType: field.type,
            hasChanges: Boolean(updatedValue),
            isPrimaryLocale,
          }
        );
        this.handleInstanceNodeEditingEnd(
          editState,
          selectedNativeIdPath,
          Boolean(isEditingCms)
        );
        return true;
      }

      const {newFieldValue} = updatedValue;

      this.dispatch(startOnCanvasEditSave());

      const doSave = () =>
        saveCollectionItemField({
          item,
          collectionId,
          fieldSlug,
          newFieldValue,
          source: 'canvas',
          aiAttribution: shouldAttributeToAi,
        })
          .then(({status}: SaveCollectionItemFieldResolution) => {
            if (status === 'CANCELLED') {
              this.dispatch(discardOnCanvasEditSave());
              return false;
            } else if (status === 'DISCARDED') {
              this.dispatch(discardOnCanvasEditSave());
              return safelyStopEditing(editState, this.stopEditingText);
            } else {
              this.dispatch(successOnCanvasEditSave());
              return safelyStopEditing(editState, this.stopEditingText);
            }
          })
          .catch((err: any) => {
            const errorPayload: OnCanvasSaveErrorPayload = {
              name: 'UnknownError',
            };
            if (err.name && err.name === 'ValidationError') {
              if (err.problems && Array.isArray(err.problems)) {
                errorPayload.name = err.name;
                errorPayload.msg =
                  err.problems.length > 0
                    ? err.problems[0]
                    : 'Unknown Validation Error';
              } else {
                errorPayload.msg = 'Unknown Validation Error';
              }
            }
            this.dispatch(dynFieldSaveOnCanvasError({fieldSlug, errorPayload}));
            return false;
          })
          .finally(() => {
            if (this.props.isEditingSelectedNodeInInstance) {
              this.handleInstanceNodeEditingEnd(
                editState,
                selectedNativeIdPath,
                Boolean(isEditingCms)
              );
            }
            // Whether Collection item field save has succeeded or not,
            // report finished latency for boundCanvasTextSavingLatency event.
            ClientLatencyTools.finishAndReport(
              TelemetryEventLabel.BOUND_CANVAS_TEXT_SAVING_LATENCY,
              {
                fieldType: field.type,
                hasChanges: Boolean(updatedValue),
                isPrimaryLocale,
              }
            );
          });

      const saveRequest = doSave().finally(() => {
        if (this.inFlightCmsSave === saveRequest) {
          this.inFlightCmsSave = null;
        }
      });
      this.inFlightCmsSave = saveRequest;
      return saveRequest;
    }
    if (this.props.isEditingSelectedNodeInInstance) {
      this.handleInstanceNodeEditingEnd(
        editState,
        selectedNativeIdPath,
        Boolean(isEditingCms)
      );
      return true;
    }
    return safelyStopEditing(editState, this.stopEditingText);
  };

  stopEditingText = () => {
    this.compositionJustResolved = false;
    this.compositionFinished = false;
    const stopEditingTextProp =
      this.props.renderProps.environmentContext.get('stopEditingText');

    if (typeof stopEditingTextProp === 'function') {
      stopEditingTextProp();
    } else {
      throw Error(
        'ContentEditor needs a `stopEditingText` function in environmentContext'
      );
    }
  };

  startEditingText() {
    const startEditingTextProp =
      this.props.renderProps.environmentContext.get('startEditingText');

    if (typeof startEditingTextProp === 'function') {
      startEditingTextProp();
    } else {
      throw Error(
        'ContentEditor needs a `startEditingText` function in environmentContext'
      );
    }
  }

  getEditTargetNativeIdPath = (): ID[] | null | undefined => {
    const {environmentContext} = this.props.renderProps;
    const editTargetFieldIdPath = environmentContext.get(
      'editTargetFieldIdPath'
    );
    const editTargetNativeIdPath = environmentContext.get(
      'editTargetNativeIdPath'
    );

    // This applies to the designer and editor canvases
    const contentEditorActiveInCanvas = isNativeIdPath(editTargetNativeIdPath);
    // This applies to the designer and editor datamanager richtext fields
    const contentEditorActiveInDataManager = isNativeIdPath(
      editTargetFieldIdPath
    );

    if (contentEditorActiveInCanvas) {
      return editTargetNativeIdPath;
    } else if (contentEditorActiveInDataManager) {
      return editTargetFieldIdPath;
    } else {
      return null;
    }
  };

  getSiteOrDatabaseId = (): string => {
    const editState = this.getEditState();
    if (!editState) {
      return '';
    }

    const {subtree} = editState;
    const useDynamicBucket =
      Boolean(getElementBindingDataPaths(subtree).innerHTML) ||
      isDynamoChildrenBinding(subtree.data);
    const site = this.props.site;
    if (site && site.get) {
      // Sites that have never used CMS/Ecommerce won't have a database
      // But if the element is bound, then it is defined and a string. So we can assert that here.
      return useDynamicBucket
        ? (site.get('database') as string)
        : site.get('id');
    } else {
      throw Error(
        'ContentEditor needs a `site` Immutable Map/Record in RenderContext with the database or site id'
      );
    }
  };

  isRichTextWithDynBoundChildren = (element: ExpressionElement) => {
    const isRichTextElement = isRichText(element);
    const hasLegacyBinding = Boolean(
      getDynBindingsFromElement(element)['innerHTML']
    );
    return isRichTextElement && hasLegacyBinding;
  };

  render() {
    const editState = this.getEditState();
    const {render, renderProps, isEditing, isEditingThisInstance, dispatch} =
      this.props;
    const {attributes, environmentContext, element, fullEnv, evalExpr} =
      renderProps;
    const isEditingAnotherInstance = isEditing && !isEditingThisInstance;
    const isNestedInTextHolderWithBoundHTML =
      this.isRichTextWithDynBoundChildren(element);
    const contentNodeEnvironmentContext = environmentContext
      .set('isNestedInTextHolder', true)
      .set(
        'isNestedInTextHolderWithBoundHTML',
        isNestedInTextHolderWithBoundHTML
      )
      .set('isEditingThisInstance', isEditingThisInstance)
      .set('isEditingAnotherInstance', isEditingAnotherInstance);
    const selectedNativeIdPath = environmentContext.get('selectedNativeIdPath');
    const isDesignSiteMode = environmentContext.get('designSiteMode');
    const isEditingPropertyInDesigner =
      isDesignSiteMode &&
      selectedNativeIdPath &&
      isPathContainingInstances(selectedNativeIdPath);

    const contentEditorValidationError = environmentContext.get(
      'contentEditorValidationError'
    );

    if (editState) {
      const {
        subtree,
        item,
        linkOnlyMode,
        validationErrorShown,
        hasServerError,
      } = editState;

      if (isEditingThisInstance) {
        const contentEditorProps: ExpansionProps = {
          ...renderProps,
          environmentContext: contentNodeEnvironmentContext,
          element: subtree,
          // Out-of-band CE subtree swaps should reset walk-state (seen/current component)
          // but keep the current eval env so static bindings still resolve.
          data: evalExpr(subtree.data)({
            ...freshEnvForReifiedSubtree(fullEnv),
            evalEnv: fullEnv.evalEnv,
          }),
          style: EDIT_STYLES,
          attributes: (attributes as Map<string, any>)
            .set('contentEditable', !linkOnlyMode)
            .set('data-editing-component-property', isEditingPropertyInDesigner)
            .set('suppressContentEditableWarning', !linkOnlyMode)
            .set('onBeforeInput', this.onBeforeInput)
            .set('onInput', this.onInput)
            .set('onKeyDown', this.onKeyDown)
            .set('onKeyUp', this.onKeyUp)
            .set('onMouseUp', this.onMouseUp)
            .set('onMouseDown', this.onMouseDown)
            .set('onFocus', this.onComponentRootFocus)
            .set('onDoubleClick', this.onDoubleClick)
            .set('onCopy', this.onCopy)
            .set('onPaste', this.onPaste)
            .set('onCut', this.onCut)
            .set('onClick', this.onClick)
            .set('onCompositionStart', this.onCompositionStart)
            .set('onCompositionEnd', this.onCompositionEnd)
            .set('spellCheck', true)
            .set('data-gramm_editor', false)
            .set('data-is-dynamic', Boolean(item))
            .set(
              'data-has-error',
              hasServerError ||
                (contentEditorValidationError && validationErrorShown)
            ),
          /** TODO (Yoav): events we're probably going to need.
                onKeyDownCapture
                onChange
                onKeyPress
                onSelect
            */
        };

        return (
          <ContentEditorErrorBoundary dispatch={dispatch}>
            {render(contentEditorProps)}
          </ContentEditorErrorBoundary>
        );
      } else if (isEditingAnotherInstance) {
        // If this is another instance of a symbol or static textHolder in a
        // dynlist, just connect the subtree here but none of the events or
        // editing styles.

        // If we're editing another instance _and_ that instance has children bound data,
        // maintain the original node/element. Otherwise, replace the subtree/subtree.
        const hasBoundData =
          isElementWithVariableOrSelectChildren(element) ||
          Boolean(getDynBindingsFromElement(element)['innerHTML']);

        const inactiveInstanceProps: ExpansionProps = {
          ...renderProps,
          environmentContext: contentNodeEnvironmentContext,
          attributes,
          ...(hasBoundData
            ? {}
            : {
                element: subtree,
                // Out-of-band CE subtree swaps should reset walk-state (seen/current component)
                // but keep the current eval env so static bindings still resolve.
                data: evalExpr(subtree.data)({
                  ...freshEnvForReifiedSubtree(fullEnv),
                  evalEnv: fullEnv.evalEnv,
                }),
              }),
        };

        return (
          <ContentEditorErrorBoundary dispatch={dispatch}>
            {render(inactiveInstanceProps)}
          </ContentEditorErrorBoundary>
        );
      }
    }

    const inactiveProps = {
      ...renderProps,
      environmentContext: contentNodeEnvironmentContext,
    } as const;

    return (
      <ContentEditorErrorBoundary dispatch={dispatch}>
        {render(inactiveProps)}
      </ContentEditorErrorBoundary>
    );
  }
}

type ContentEditorWrapperProps = Omit<
  PropsType,
  | 'dispatch'
  | 'site'
  | 'runtimeEnvironment'
  | 'isOpenCanvas'
  | 'contentEditorViewportAdapter'
  | 'getContentEditorLiveSnapshot'
>;

export default function ContentEditorWrapper(props: ContentEditorWrapperProps) {
  const dispatch = useDispatch();
  const site = useSite();
  const runtimeEnvironment = useRuntimeEnvironment();
  const editTargetFieldIdPath = useEditTargetFieldIdPath();
  const contentEditorViewportAdapter = useContentEditorViewportAdapter();
  const getContentEditorLiveSnapshot = useContentEditorLiveSnapshot();
  const contentEditorFrameId = useContentEditorFrameIdWhen(
    props.isEditingThisInstance
  );

  return (
    <ContentEditor
      render={props.render}
      renderProps={props.renderProps}
      isEditingThisInstance={props.isEditingThisInstance}
      isEditing={props.isEditing}
      nativeId={props.nativeId}
      isEditingSelectedNodeInInstance={props.isEditingSelectedNodeInInstance}
      latencyProfilerEventName={props.latencyProfilerEventName}
      dispatch={dispatch}
      site={site}
      runtimeEnvironment={runtimeEnvironment}
      editTargetFieldIdPath={editTargetFieldIdPath}
      contentEditorViewportAdapter={contentEditorViewportAdapter}
      getContentEditorLiveSnapshot={getContentEditorLiveSnapshot}
      contentEditorFrameId={contentEditorFrameId}
    />
  );
}

const isVariableOrSelect: (val: Expression) => boolean = matchExpression({
  Variable: constantTrue,
  Select: constantTrue,
  Call: (call) => isVariableOrSelect(call.fun),
  _: constantFalse,
});

const isElementWithVariableOrSelectChildren = (
  element: ExpressionElement
): boolean => {
  const elementChildren = Expressions.getIn(
    Expressions.getElementData(element),
    [{in: 'Record', at: 'children'}]
  );
  if (!elementChildren) return false;

  return isVariableOrSelect(elementChildren);
};
